import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { submitResult } from "@/lib/resultOutbox";
import { prefetch as prefetchAnswerKey, resolve as resolveAnswer, peekCorrectIndex } from "@/lib/questionCache";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Trophy, AlertTriangle, RefreshCw, ListChecks, LogOut, X, Loader2 } from "lucide-react";
import { PauseOverlay } from "@/components/gameplay/PauseOverlay";
import { WonScreen, LostScreen, ExhaustedScreen } from "@/components/gameplay/ResultScreens";
import { fireSupertConfetti, fireVictoryConfetti, fireFinalistConfetti, playFinalistFanfare } from "@/components/gameplay/celebrations";
import { toast } from "sonner";
import { fetchHomepageConfig, readHomepageConfigCacheSync } from "@/lib/homepageConfigCache";
import AvatarBuilder from "@/components/AvatarBuilder";
import LogoutButton from "@/components/LogoutButton";

import RankShareDialog from "@/components/RankShareDialog";
import GameToolbar from "@/components/gameplay/GameToolbar";
import GameplayBrandCorner from "@/components/gameplay/GameplayBrandCorner";
import ReadyModal from "@/components/gameplay/modals/ReadyModal";
import LivesLostModal from "@/components/gameplay/modals/LivesLostModal";
import ConfirmExitModal from "@/components/gameplay/modals/ConfirmExitModal";
import VsHumanCountdown321 from "@/components/gameplay/modals/VsHumanCountdown321";
import { AvatarSVG, DEFAULT_AVATAR } from "@/lib/avatarBuilder";
import { playSound, unlockAudio, getMuted, subscribeMuted, setEmbedMuted } from "@/lib/sounds";
import { getBackgroundCss } from "@/lib/gameVisual";
import { useGameTimer } from "@/hooks/useGameTimer";
import { useVsRobotFinish } from "@/hooks/useVsRobotFinish";
import { useVsRobotDuel } from "@/hooks/useVsRobotDuel";
import { useVsHumanDuel } from "@/hooks/useVsHumanDuel";
import { useRouletteSnippets } from "@/hooks/useRouletteSnippets";
import { useDemoRunner } from "@/hooks/useDemoRunner";
import DemoOverlay from "@/components/gameplay/DemoOverlay";
import { MiniRouletteCard, BRAND_ASSETS } from "@/components/gameplay/parts";
import { getOtherGameRoute, getExitRoute, getBackRoute } from "@/lib/gamePlayNavigation";
import { isWinningProgress, localSupertAward } from "@/lib/gamePlayMath";
import {
  SUPERT,
  POSITIONS,
  formatTime,
  RouletteCard,
  CardFront,
  Options,
  BrandBox,
  AttemptsPill,
  TimerPill,
  RobotProfileBox,
  HumanOpponentBox,
  RaceBar,
  FloatingAwards,
  SupertBar,
  ActivitySummary,
  ProfileCard,
  Modal,
  warmupGameAssets,
  warmupCriticalAssets,
} from "@/components/gameplay/parts";
import { pauseRasterQueue } from "@/lib/outlineRaster";
import { useVsHumanContext } from "@/lib/vsHumanSocket";

// Local FALLBACK only — used until the live config from /api/config/quadriculas
// resolves. This must mirror `/app/shared/quadriculas.json`, which is the
// single source of truth (backend reads it too).
const QUADS_FALLBACK = {
  original: {
    1: ["A", "B", "C"],
    2: ["A", "B", "C", "C", "D", "D", "E", "E"],
    3: ["A", "B", "C", "D", "E", "F", "F", "G", "G"],
    4: ["D", "D", "E", "E", "F", "F", "F", "G", "G", "G", "H", "H", "H", "H", "I", "I", "I", "I", "J", "J", "J", "J"],
    5: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"],
  },
  quiz: {
    1: ["A", "B"],
    2: ["A", "B", "C", "C", "D", "D"],
    3: ["A", "B", "C", "C", "D", "D", "E", "E", "E", "F", "F", "F", "G", "G", "G"],
    4: ["A", "B", "C", "D", "E", "F", "G"],
  },
};
// In-memory cache populated on first fetch from /api/config/quadriculas. The
// `pickFromQuad` helper always reads through `QUADS` so any later refresh
// (very rare — server-side admin tweak) is picked up automatically.
let QUADS = {
  original: { ...QUADS_FALLBACK.original },
  quiz: { ...QUADS_FALLBACK.quiz },
};

// Active format pointer — set by GamePlay once the session loads. Defaults
// to "original" for backwards compatibility (legacy games / training).
let ACTIVE_FORMAT = "original";

// Fire-and-forget telemetry: report each spin so the admin Roleta panel
// can show observed-vs-theoretical probabilities. Failures are silenced —
// gameplay must never block on this.
const trackSpin = (level, position) => {
  try {
    api.post("/roulette/track", { level: Number(level), position, format: ACTIVE_FORMAT }).catch(() => {});
  } catch (_) { /* ignore */ }
};

// Filler draw for the non-active roulette cells (NOT tracked — telemetry
// records only the real landing, see animateSpin).
const pickFromQuad = (level) => {
  const arr = poolForLevel(level);
  return arr[Math.floor(Math.random() * arr.length)];
};

// Weighted roulette pool (repeats = weights) for `level` in the active format.
const poolForLevel = (level) => {
  const pool = QUADS[ACTIVE_FORMAT] || QUADS.original;
  const fallbackLevel = ACTIVE_FORMAT === "quiz" ? 4 : 5;
  return pool[level] || pool[fallbackLevel];
};

export default function GamePlay() {
  const { gameId } = useParams();
  const [params] = useSearchParams();
  const mode = params.get("mode") || "training";
  // Embed mode (`?embed=1`): used by the home-page tablet overlay. Hides
  // all chrome (brand corner, logout, bottom toolbar), auto-starts the
  // demo as soon as the session loads, and loops it on finish — so the
  // tablet in the hero plays the actual in-site demo on repeat.
  const isEmbed = params.get("embed") === "1";
  // The hero tablet demo must ALWAYS be silent (user requirement). Flip the
  // per-document embed mute as early as possible so no demo sound ever plays,
  // without affecting the user's global sound preference on the main site.
  if (isEmbed) setEmbedMuted(true);
  // DEMO ("Aprender a Jogar") is a client-only overlay on top of the training
  // engine. When true: user clicks are ignored (auto-play robot),
  // result submission is skipped, and guide phrases are shown between
  // roulette spin → question → answer → letter award.
  const [isDemoMode, setIsDemoMode] = useState(() => params.get("demo") === "1");
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const { auth, logout } = useAuth();
  const vshCtx = useVsHumanContext();
  const demoRunner = useDemoRunner({ enabled: isDemoMode, lang });
  const demoRoundRef = useRef(0); // rounds completed in the demo script
  // When the DEMO reaches its final SUPERT win, this flips to true. Any
  // scheduled or pending guide phrase is cancelled and the auto-play
  // effect short-circuits so no further phrases (including phrase_1)
  // ever appear until the user taps SAIR DEMO (which unmounts the tree).
  const demoFinishedRef = useRef(false);
  const demoTimersRef = useRef([]);
  const clearDemoTimers = useCallback(() => {
    demoTimersRef.current.forEach((t) => clearTimeout(t));
    demoTimersRef.current = [];
  }, []);
  useEffect(() => () => clearDemoTimers(), [clearDemoTimers]);

  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("ready"); // ready | playing | paused | won | lost | exhausted
  const [round, setRound] = useState(0);
  // Ref mirror so refs-only callbacks (animateSpin) can read the current
  // round without adding it to their deps and forcing re-creations.
  const roundRef = useRef(0);
  useEffect(() => { roundRef.current = round; }, [round]);
  const [quadriculas, setQuadriculas] = useState([null, null, null, null, null]);
  const [spinning, setSpinning] = useState(false);
  // True while the READY modal waits for the asset warmup before starting.
  const [preparing, setPreparing] = useState(false);
  const [progress, setProgress] = useState([]);
  const [reveal, setReveal] = useState(null);
  const [feedback, setFeedback] = useState(null);
  // Timer state + controls (start/stop/pause/resume/reset/addPenalty/getElapsed).
  const {
    start: startTimer,
    stop: stopTimer,
    pause: pauseTimer,
    resume: resumeTimer,
    reset: resetTimer,
    addPenalty: addTimerPenalty,
    getElapsed: getTimerElapsed,
  } = useGameTimer();
  const [busy, setBusy] = useState(false);
  // Lives system: how many wrong answers consumed so far + the pending "lost a life"
  // modal. `livesModal` is null when no warning is showing.
  const [livesUsed, setLivesUsed] = useState(0);
  const [livesModal, setLivesModal] = useState(null); // {used, total, penalty}
  // 600ms amber pulse + "+Ns" floating badge over the timer pill right after a
  // wrong answer (rendered BEFORE the lives modal so the student visually sees
  // their clock jump forward).
  const [timerPenaltyFx, setTimerPenaltyFx] = useState({ active: false, amount: 0 });
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [avatarCfg, setAvatarCfg] = useState(() => {
    try {
      const raw = sessionStorage.getItem("player_avatar_cfg") || localStorage.getItem("player_avatar_cfg");
      if (raw) return { ...DEFAULT_AVATAR, ...JSON.parse(raw) };
    } catch (e) { console.error("[avatar_cfg parse]", e); }
    return { ...DEFAULT_AVATAR };
  });
  // Question pool management
  // NOTE: displayed-question + snippet state now owned by useRouletteSnippets.
  // Multi-SUPERT cycles
  const [supertCycle, setSupertCycle] = useState(0); // completed SUPERTs so far in this session
  const [pauseCountdown, setPauseCountdown] = useState(null); // null or 5..0
  const [supertFlash, setSupertFlash] = useState(false);
  const [repeatLetter, setRepeatLetter] = useState(null);
  // Opponent-side "letra repetida" state — mirrors the player's `repeatLetter`
  // for VS ROBOT / VS HUMANO so the adjacent-duplicate animation also plays
  // in the opponent's SupertBar when they earn a repeated letter.
  const [oppRepeatLetter, setOppRepeatLetter] = useState(null);
  const [repeatsSoFar, setRepeatsSoFar] = useState(0);
  const [celebratingSupert, setCelebratingSupert] = useState(false);
  // Final-win personal celebration — "Parabéns, {nome}!" shown during the
  // 3-second wave for competition + vs_human modes (user request 2026-02).
  const [celebratingFinalWin, setCelebratingFinalWin] = useState(false);
  const [streak, setStreak] = useState(0);
  const supertBarRef = useRef(null);
  // ----- Shared per-round refs (used by VS ROBOT and VS HUMANO) -----
  const roundStartRef = useRef(0);
  const userClickRef = useRef(null); // latest user click info (mutable for setTimeout resolver)
  const roundResolvedRef = useRef(false); // synchronous gate to ignore late clicks
  // VS HUMANO CARD=ROUND: filled by useRouletteSnippets with the function
  // that paints the deterministic (card, position) for a given round index.
  const paintVshRoundRef = useRef(null);
  const answerInFlightRef = useRef(false); // synchronous anti-double-click gate (survives React batch)
  const progressRef = useRef([]); // mirrors `progress` so setTimeout-fired resolvers read fresh values
  // Mirrors `activeQuestion` so the VS-ROBOT robot-timer callback can reveal
  // the correct option index even when the user never clicked (robot-first).
  const activeQuestionRef = useRef(null);
  // Competition end summary
  const [finishData, setFinishData] = useState(null); // {attempts_available, victories, defeats, best_time}
  const [history, setHistory] = useState(null); // {credential, attempts}
  const [cardLogo, setCardLogo] = useState(null);
  const [cardLogoCfg, setCardLogoCfg] = useState({ height: 44, offsetX: -10, offsetY: 10 });
  // Homepage config for the shared HomeHeader (logo, nav, language picker, SAIR)
  const [homeCfg, setHomeCfg] = useState(() => readHomepageConfigCacheSync() || {});
  useEffect(() => {
    fetchHomepageConfig().then(setHomeCfg).catch(() => setHomeCfg({}));
  }, []);
  const [muted, setMutedState] = useState(() => getMuted());
  useEffect(() => subscribeMuted((v) => setMutedState(v)), []);
  const [xp, setXp] = useState(0);
  const [bestTime, setBestTime] = useState(null); // best overall completed time
  const spinTickRef = useRef(null);
  const countdownRef = useRef(null);
  const initRanRef = useRef(false);

  const profile = {
    name: sessionStorage.getItem("player_name") || "Anon",
    nationality: sessionStorage.getItem("player_nationality") || "PT",
    // School is read from sessionStorage FIRST. We only fall back to
    // localStorage for competition / eliminatoria modes where the player
    // persists across tabs via localStorage (CompetitionLobby writes it).
    // For training / vs_robot / vs_human we deliberately skip the
    // localStorage fallback so a previous player's school (e.g. a
    // competition entry made earlier on the same browser) never leaks
    // into a brand-new training profile that chose not to fill it in.
    school:
      sessionStorage.getItem("player_school") ||
      ((mode === "competition" || mode === "eliminatoria")
        ? (localStorage.getItem("player_school") || "")
        : ""),
  };

  // Player's difficulty level (1..5):
  //   • Competition mode  → from JWT/auth claims (locked per credential).
  //   • Eliminatoria mode → from URL `?level=N` (set by the lobby based on the
  //     chosen tier). Falls back to sessionStorage for back-compat.
  //   • Training/VS Robot/VS Human → from sessionStorage `player_level`.
  const playerLevel = (() => {
    if (mode === "competition") return auth?.level || 1;
    const urlLevel = parseInt(params.get("level") || "", 10);
    if (urlLevel >= 1 && urlLevel <= 5) return urlLevel;
    return parseInt(sessionStorage.getItem("player_level") || "1", 10);
  })();
  // Ref mirror so stable callbacks (animateSpin) always read the RESOLVED
  // level (auth/URL/sessionStorage) instead of sessionStorage alone — the
  // two can diverge in competition/eliminatória modes and a mismatch made
  // the roulette land on a random position of the player's row.
  const playerLevelRef = useRef(playerLevel);
  useEffect(() => { playerLevelRef.current = playerLevel; });

  // Display label for the player's level. When the game uses tiers (escalões),
  // we show "<tier name> (Nível N)" so two tiers sharing the same level
  // (e.g. "ESCALÃO 6" and "ESCALÃO 9" both at level 4) stay distinguishable
  // and the underlying difficulty is always visible.
  const levelLabel = (() => {
    const game = session?.game;
    if (game?.level_system === "tiers" && Array.isArray(game.tiers)) {
      // Priority order to locate the player's tier:
      //   1. URL `?tier=<tier_id>` (set by Lobby / re-attempts)
      //   2. JWT/auth claims (competition mode)
      //   3. sessionStorage (back-compat)
      //   4. fallback: first tier matching the resolved level
      const urlTierId = params.get("tier");
      const storedTierId =
        urlTierId ||
        (mode === "competition"
          ? (auth?.tier_id || auth?.credential?.tier_id)
          : sessionStorage.getItem("player_tier_id"));
      let tier = storedTierId && game.tiers.find((tt) => tt.id === storedTierId);
      if (!tier) tier = game.tiers.find((tt) => Number(tt.level) === Number(playerLevel));
      if (tier?.name) {
        const lvl = Number(tier.level || playerLevel);
        // Localize the tier name using its `translations` map, falling back to
        // the canonical PT value when no override exists for the current lang.
        const tierName = (tier.translations?.[lang]?.name) || tier.name;
        return `${tierName} (${t("level_paren")} ${lvl})`;
      }
    }
    return `${t("level_paren")} ${playerLevel}`;
  })();

  // Player identity block shown on the "ELIMINATÓRIA SUPERADA!" window so a
  // passed knockout always displays who passed it, for which game and tier.
  const elimGameName = session?.game?.translations?.[lang]?.name || session?.game?.name || "";
  const elimInfo = {
    name: profile.name,
    school: profile.school || "",
    game: elimGameName,
    level: levelLabel,
  };

  // Card logo: controlled EXCLUSIVELY per-game via `game.visual.card_logo_*`.
  // (The former global "Cartas & Marca" admin panel was removed — see chat
  // history — so we no longer fetch/apply defaults from `/api/footer`. When a
  // game has no `card_logo_image` set, NO logo is rendered on the cards.)
  useEffect(() => {
    const v = session?.game?.visual;
    if (!v) return;
    setCardLogo(v.card_logo_image || null);
    setCardLogoCfg({
      // Per-card visibility toggles. Default true so games created before
      // the toggle feature keep showing the logo on both cards.
      frontEnabled: v.card_logo_front_enabled !== false,
      rouletteEnabled: v.card_logo_roulette_enabled !== false,
      frontHeight: v.card_logo_front_height ?? 44,
      rouletteHeight: v.card_logo_roulette_height ?? 44,
      frontX: v.card_logo_front_offset_x ?? -10,
      frontY: v.card_logo_front_offset_y ?? 10,
      rouletteX: v.card_logo_roulette_offset_x ?? -10,
      rouletteY: v.card_logo_roulette_offset_y ?? 10,
      hue: v.card_logo_hue ?? 0,
      saturate: v.card_logo_saturate ?? 1,
      brightness: v.card_logo_brightness ?? 1,
      invert: v.card_logo_invert ?? false,
    });
  }, [session?.game?.id]);

  // PERF: warm gameplay assets while the READY modal is on screen. The
  // CRITICAL set (all letter PNG downloads + small-letter rasters + the
  // initial reveal char raster) gates `beginGame`; the FULL set continues in
  // the background so later rounds render instantly too.
  const initialIdleCharRef = useRef(null);
  if (initialIdleCharRef.current === null) {
    const pool = [...SUPERT, "*"];
    initialIdleCharRef.current = pool[Math.floor(Math.random() * pool.length)];
  }
  const warmupRef = useRef(Promise.resolve());
  useEffect(() => {
    if (!session?.game) return;
    const visual = session.game.visual || {};
    warmupRef.current = warmupCriticalAssets(visual, initialIdleCharRef.current);
    warmupRef.current.then(() => warmupGameAssets(visual)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.game?.id]);

  // Fetch the live QUADS config from the backend ONCE per app load. Falls
  // back gracefully to the hardcoded `QUADS_FALLBACK` if the call fails.
  // Loads BOTH formats (original + quiz) into the cache so any game can pick.
  useEffect(() => {
    api.get("/config/quadriculas")
      .then((r) => {
        const formats = r.data?.formats;
        if (formats && typeof formats === "object") {
          for (const fmt of ["original", "quiz"]) {
            const lvls = formats[fmt]?.levels;
            if (lvls && typeof lvls === "object") {
              const next = {};
              for (const k of Object.keys(lvls)) {
                const n = Number(k);
                if (Array.isArray(lvls[k]) && lvls[k].length && n >= 1 && n <= 5) {
                  next[n] = lvls[k].map(String);
                }
              }
              if (Object.keys(next).length) Object.assign(QUADS[fmt], next);
            }
          }
        } else {
          // Legacy response shape (pre-format) — treat as ORIGINAL.
          const lvls = r.data?.levels;
          if (lvls && typeof lvls === "object") {
            const next = {};
            for (const k of Object.keys(lvls)) {
              const n = Number(k);
              if (Array.isArray(lvls[k]) && lvls[k].length && n >= 1 && n <= 5) {
                next[n] = lvls[k].map(String);
              }
            }
            if (Object.keys(next).length) Object.assign(QUADS.original, next);
          }
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  // Switch the active format whenever the loaded session reports one.
  useEffect(() => {
    ACTIVE_FORMAT = session?.game?.format === "quiz" ? "quiz" : "original";
  }, [session?.game?.format]);

  // Fetch derived XP (refreshes when phase changes — e.g., after a victory).
  useEffect(() => {
    const params = {};
    if (mode === "competition" && auth?.credential_id) params.credential_id = auth.credential_id;
    else if (mode === "training") {
      const tid = sessionStorage.getItem("training_user_id");
      if (tid) params.training_user_id = tid;
    }
    if (!params.credential_id && !params.training_user_id) return;
    api.get("/avatar/progress", { params })
      .then((r) => setXp(Number(r.data?.xp || 0)))
      .catch((err) => { console.error("[avatar/progress]", err); });
    // Player's best successfully-completed time (across all games)
    api.get("/player/best-time", { params })
      .then((r) => setBestTime(r.data?.best_time ?? null))
      .catch((err) => { console.error("[player/best-time]", err); });
  }, [mode, auth?.credential_id, phase]);

  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;
    // Guard: competition mode must go through lobby + teacher password before reaching /jogar
    if (mode === "competition" && !sessionStorage.getItem(`teacher_verified_${gameId}`)) {
      nav(`/lobby/${gameId}`);
      return;
    }
    const init = async () => {
      try {
        // ---- VS ROBOT public play: no auth, uses /vs-robot/start ----
        if (mode === "vs_robot") {
          const robotId = params.get("robot");
          const elimId = params.get("elim") || undefined;
          if (!robotId) { nav("/vs-robot"); return; }
          const { data } = await api.get("/vs-robot/start", {
            params: { game_id: gameId, robot_id: robotId, lang, ...(elimId ? { elim: elimId } : {}) },
          });
          setSession(data);
          // PERF: prefetch the answer key so every answer resolves LOCALLY
          // (no per-round network round-trip — critical in production).
          prefetchAnswerKey(gameId);
          return;
        }
        // ---- VS HUMANO: session was prepared by the lobby via WS match_start ----
        if (mode === "vs_human") {
          const cached = sessionStorage.getItem("vsh_session");
          if (!cached) {
            // No session — kick back to lobby. This shouldn't happen unless the
            // user typed the URL by hand.
            nav("/vs-humano");
            return;
          }
          let parsed;
          try { parsed = JSON.parse(cached); }
          catch { nav("/vs-humano"); return; }
          // Build a session-shaped object compatible with the rest of GamePlay.
          // `questions` mirrors vs_robot's by-position dict so existing code paths
          // (CardFront, RouletteCard) keep working unchanged.
          setSession({
            session_id: parsed.session_id,
            game: parsed.game,
            questions: parsed.questions || {},
            // CARD=ROUND (Mar 2026): server-authoritative deck + pre-baked
            // round list shared by BOTH peers — consumed strictly by index.
            cards: Array.isArray(parsed.cards) ? parsed.cards : [],
            rounds: Array.isArray(parsed.rounds) ? parsed.rounds : [],
          });
          sequenceRef.current = Array.isArray(parsed.sequence) ? parsed.sequence : [];
          // 2026-02: each peer plays their OWN escalão/nível. The opponent
          // may be at a DIFFERENT level → attach their level/tier to the
          // opponent object so `HumanOpponentBox` labels them correctly.
          setVshOpponent(parsed.opponent
            ? {
                ...parsed.opponent,
                level: parsed.opponent_level ?? parsed.opponent.level ?? null,
                tier_id: parsed.opponent_tier_id || parsed.opponent.tier_id || null,
              }
            : null);
          // Clear single-use cache so a refresh doesn't replay a dead session.
          sessionStorage.removeItem("vsh_session");
          return;
        }
        // ---- Eliminatórias self-service: session was created in the lobby ----
        if (mode === "eliminatoria") {
          const cached = sessionStorage.getItem("elim_session");
          if (cached) {
            const data = JSON.parse(cached);
            sessionStorage.removeItem("elim_session"); // single-use
            setSession(data);
            prefetchAnswerKey(gameId);
            return;
          }
          // Fallback: lobby was skipped — recreate via API.
          const elimId = params.get("elim");
          if (!elimId) { nav("/eliminatorias/lobby"); return; }
          const { data } = await api.post(`/eliminatorias/${elimId}/play/start`, null, { params: { lang } });
          setSession(data);
          prefetchAnswerKey(gameId);
          return;
        }
        const payload = { game_id: gameId, mode };
        // GRANDE FINAL (Feb-2026): URL `?gf=1` → tell the backend to
        // bypass credentials AND skip persistence (session_id prefixed
        // `gf-`). From the frontend's POV the game plays exactly like a
        // normal training round.
        const isGrandeFinal = params.get("gf") === "1";
        if (isGrandeFinal) payload.grande_final = true;
        if (mode === "training") {
          payload.name = profile.name;
          payload.school = profile.school || "";
          payload.nationality = profile.nationality;
          payload.training_user_id = sessionStorage.getItem("training_user_id");
        } else {
          payload.credential_id = auth?.credential_id;
        }
        const { data } = await api.post("/play/start", payload, { params: { lang } });
        setSession(data);
        // Offline-play: kick off a background fetch of the per-game answer key
        // so subsequent rounds can validate locally without a network hop.
        // Failure is non-blocking — the answer resolver falls back to the
        // network call if the cache isn't populated.
        prefetchAnswerKey(gameId);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const isExhausted =
          (typeof detail === "object" && detail?.code === "no_attempts") ||
          (typeof detail === "string" && /sem tentativas|tentativas|esgotadas|attempts|intentos/i.test(detail));
        const msg = typeof detail === "object" ? (detail.message || "Erro ao iniciar") : (detail || "Erro ao iniciar");
        toast.error(msg);
        if (mode === "competition" && isExhausted) {
          // Build a minimal session shell so the modal renders within GamePlay layout
          setSession({ game: { name: "", visual: {}, supert_count: 1 }, questions: {} });
          setPhase("exhausted");
          if (auth?.credential_id) {
            try {
              const h = await api.get(`/credentials/${auth.credential_id}/attempts`);
              setHistory(h.data);
            } catch (e) { console.error("[credentials.attempts]", e); }
          }
        } else {
          nav("/");
        }
      }
    };
    init();
    return () => { stopTimer(); if (spinTickRef.current) clearInterval(spinTickRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, mode, lang]);

  // Expose the currently-running COMPETITION attempt to `window` so that a
  // "SAIR / Terminar sessão" click (LogoutButton → logoutSession) can fire a
  // final /play/finish { status: "lost" } via sendBeacon BEFORE the storage
  // wipe + redirect. Without this, mid-game logout would leave the attempt
  // `in_progress` forever and `attempts_available` would not decrement.
  //
  // The SAME mechanism now also fires on tab close / refresh / navigation
  // via a `pagehide` + `beforeunload` handler below — so no one can escape
  // an in-progress competition attempt without consuming an attempt.
  useEffect(() => {
    const isLive = mode === "competition" && phase === "playing" && session?.session_id;
    if (isLive) {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      window.__supertActiveCompetition = {
        session_id: session.session_id,
        game_id: gameId,
        backend,
        get time_seconds() { try { return getTimerElapsed() || 0; } catch { return 0; } },
      };
    } else {
      try { delete window.__supertActiveCompetition; } catch { /* ignore */ }
    }
    return () => { try { delete window.__supertActiveCompetition; } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, phase, session?.session_id, gameId]);

  // Tab-close / refresh / navigation forfeit — competition mode only.
  // Fires a fire-and-forget `sendBeacon` to /api/play/finish with
  // status="lost" the instant the browser dispatches `pagehide` (the most
  // reliable "the page is going away" signal per WHATWG). `beforeunload`
  // is registered as a secondary safety net for older browsers. A shared
  // flag prevents double-submission when both fire.
  useEffect(() => {
    if (mode !== "competition" || phase !== "playing" || !session?.session_id) return undefined;
    let fired = false;
    const fireForfeit = () => {
      if (fired) return;
      const info = window.__supertActiveCompetition;
      if (!info || !info.session_id || !info.game_id || !info.backend) return;
      fired = true;
      const payload = JSON.stringify({
        session_id: info.session_id,
        game_id: info.game_id,
        time_seconds: Number(info.time_seconds) || 0,
        status: "lost",
        client_submission_id: `tabclose-${info.session_id}`,
      });
      const url = `${info.backend}/api/play/finish`;
      try {
        const blob = new Blob([payload], { type: "application/json" });
        if (!navigator.sendBeacon(url, blob)) {
          // Beacon rejected (e.g. payload too big) — fall back to keepalive fetch.
          fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: payload,
            keepalive: true,
          }).catch(() => { /* ignore */ });
        }
      } catch { /* ignore */ }
    };
    // `pagehide` is dispatched even when the page enters the bfcache (Safari)
    // and is the canonical signal per HTML spec. `beforeunload` covers legacy
    // browsers. Both are wired defensively.
    window.addEventListener("pagehide", fireForfeit);
    window.addEventListener("beforeunload", fireForfeit);
    return () => {
      window.removeEventListener("pagehide", fireForfeit);
      window.removeEventListener("beforeunload", fireForfeit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, phase, session?.session_id]);

  // Ref pointing to the current game's card-sound settings so `animateSpin`
  // can fire the sound at the EXACT frame the new roulette+main cards are
  // committed to the DOM (previously the callers played it hundreds of ms
  // BEFORE animateSpin, which felt out-of-sync with the visual reveal).
  const cardSoundRef = useRef({ name: "swipe", volume: 0.7 });
  useEffect(() => {
    cardSoundRef.current = {
      name: session?.game?.card_sound || "swipe",
      volume: session?.game?.card_sound_volume ?? 0.7,
    };
  }, [session?.game?.card_sound, session?.game?.card_sound_volume]);

  // Animated spin: DEPRECATED — the random-letter cycling effect was removed.
  // The final quadriculas are now set immediately. `spinning` still briefly
  // transitions true → false in the next frame so downstream hooks (useRouletteSnippets,
  // useVsHumanDuel, useVsRobotDuel) that observe that transition keep working.
  // `forceSlot` (optional): {level: int, position: str} — forces
  // `quadriculas[level-1]` to land on `position`. Used by VS HUMANO to lock the
  // active question to the deterministic sequence shared between peers.
  const animateSpin = useCallback((forceSlot, opts) => {
    const silent = !!(opts && opts.silent);
    if (spinTickRef.current) clearInterval(spinTickRef.current);
    setSpinning(true);
    const finalValues = [pickFromQuad(1), pickFromQuad(2), pickFromQuad(3), pickFromQuad(4), pickFromQuad(5)];
    // STRICT NO-REPEAT: for solo / VS ROBOT modes, ask the roulette hook for
    // the next card's landing question. The landing letter is drawn from the
    // player's LEVEL quadrícula (weighted) restricted to the card's positions —
    // so level 1 only ever lands on A/B/C — and the spin stops exactly there.
    // VS HUMAN gets its forceSlot from the server-baked rounds via the caller.
    if (!forceSlot) {
      const next = rouletteNextRoundRef.current;
      const lvl = playerLevelRef.current || 1;
      const upcoming = typeof next === "function" ? next(poolForLevel(lvl)) : null;
      if (upcoming && upcoming.position) {
        forceSlot = { level: lvl, position: upcoming.position };
      }
    }
    if (forceSlot && forceSlot.position) {
      const lvlIdx = Math.max(0, Math.min(4, (forceSlot.level || 1) - 1));
      finalValues[lvlIdx] = forceSlot.position;
    }
    // Telemetry: the ONE letter the roulette actually landed on (player's level).
    {
      const lvl = Math.max(1, Math.min(5, (forceSlot && forceSlot.level) || playerLevelRef.current || 1));
      trackSpin(lvl, finalValues[lvl - 1]);
    }
    setQuadriculas(finalValues);
    // Yield TWO frames so that:
    //   • the first rAF runs before the browser has painted the new roulette
    //     + main card into the DOM (React has committed but paint is pending);
    //   • the second rAF fires AFTER the browser has actually painted the
    //     cards on screen — that is the exact moment the user SEES the cards
    //     appear, so playing the "new card" sound here makes it sync perfectly
    //     with the visual entrance.
    // We also flip `spinning` back to false on the first rAF to preserve the
    // true→false transition observed by useRouletteSnippets and the two duel
    // hooks.
    requestAnimationFrame(() => {
      setSpinning(false);
      requestAnimationFrame(() => {
        if (silent) return;
        const s = cardSoundRef.current || {};
        if (s.name) playSound(s.name, s.volume);
      });
    });
  }, []);

  // Gate the READY → PLAYING transition on the asset warmup.
  //
  // Prior versions waited up to 6 s (later 1.5 s) for the letter PNGs to be
  // downloaded + rasterized with their outline filter, so the very first
  // paint after "INICIAR JOGO" would be the definitive one. The problem: on
  // a cold cache users saw a long "thinking" state after clicking.
  //
  // Analysis: the fallback path in `OutlinedImage` renders the same PNG with
  // the live CSS drop-shadow filter — VISUALLY IDENTICAL to the rasterized
  // version, just heavier per repaint. Combined with `pauseRasterQueue(1200)`
  // in `beginGame` (which reserves the main thread for the first paint) we
  // have zero reason to make the user wait synchronously. The rasterization
  // continues in the background and each letter swaps to its PNG version as
  // it completes, without any visible flash.
  //
  // The warmup itself is kicked off in a useEffect the moment `session.game`
  // becomes available (i.e. while the READY modal is being shown), so by the
  // time most players click "INICIAR JOGO" the critical assets are already
  // cached anyway.
  const startWhenReady = () => {
    if (preparing) return;
    setPreparing(true);
    // No async wait — start immediately. `beginGame` sets state synchronously
    // and framer-motion drives the visual transition.
    setPreparing(false);
    // Competition mode: show a 5-4-3-2-1 countdown before the first question
    // appears (schools requested this so students can settle into position).
    if (mode === "competition" && !isDemoMode) {
      setCompCountdown(5);
      return;
    }
    beginGame();
  };

  // DEMO ("Aprender a Jogar"): entered from the ReadyModal via the DEMO
  // button. Enables auto-play, disables user clicks and guide-phrase
  // overlays. The session is reused as-is — only the internal behaviour
  // flips. `beginGame` is called immediately (no competition countdown).
  const startDemo = () => {
    if (preparing) return;
    setIsDemoMode(true);
    demoRoundRef.current = 0;
    demoFinishedRef.current = false;
    // Skip competition countdown even in competition mode — DEMO is always
    // a quick guided run regardless of the origin game type.
    setCompCountdown(null);
    beginGame();
  };

  // Embed mode (home-page tablet overlay):
  //   • auto-starts the demo as soon as the session is ready,
  //   • restarts automatically on win/lose so the demo plays on repeat.
  useEffect(() => {
    if (!isEmbed) return;
    if (!session) return;
    if (phase === "ready" && !preparing) {
      startDemo();
      return;
    }
    if (phase === "won" || phase === "lost") {
      const t = setTimeout(() => {
        demoRoundRef.current = 0;
        demoFinishedRef.current = false;
        clearDemoTimers();
        setProgress([]);
        setReveal(null);
        setFeedback(null);
        setRound(0);
        resetTimer();
        setQuadriculas([null, null, null, null, null]);
        setSupertCycle(0);
        setPauseCountdown(null);
        setSupertFlash(false);
        setRepeatsSoFar(0);
        setStreak(0);
        setFinishData(null);
        setHistory(null);
        setIsDemoMode(true);
        beginGame();
      }, 1800);
      return () => clearTimeout(t);
    }
  }, [isEmbed, session, phase, preparing]); // eslint-disable-line react-hooks/exhaustive-deps

  const beginGame = () => {
    unlockAudio();
    // Give the start transition ~1.2s of raster-free main thread so the
    // spinning→false rAF flip + first paint aren't delayed by background
    // rasterization jobs (they caused visible "—" answer placeholders).
    pauseRasterQueue(1200);    setPhase("playing");
    setProgress([]);
    setReveal(null);
    setFeedback(null);
    setRound(1);
    setSupertCycle(0);
    setPauseCountdown(null);
    setSupertFlash(false);
    setRepeatsSoFar(0);
    setStreak(0);
    setLivesUsed(0);
    setLivesModal(null);
    // VS ROBOT: reset duel state
    if (mode === "vs_robot") {
      resetVsRobotState();
      userClickRef.current = null;
      roundResolvedRef.current = false;
    }
    // VS HUMANO: reset duel state
    if (mode === "vs_human") {
      resetVsHumanState();
      userClickRef.current = null;
      roundResolvedRef.current = false;
    }
    // The card "whoosh" sound must NOT play together with the click on
    // INICIAR JOGO — it should only be heard AFTER the ready modal has
    // finished its exit animation and the game cards are actually visible.
    // We therefore ask animateSpin to run silently and schedule the sound
    // ourselves ~380ms later (framer-motion default exit ≈ 300ms + a small
    // safety margin so the cards are already painted).
    if (mode === "vs_human") {
      // CARD=ROUND: paint round 0 from the server's pre-baked rounds list
      // (both peers paint the SAME card + landing). Legacy fallback: flat
      // sequence index 0.
      const firstLanding = typeof paintVshRoundRef.current === "function" ? paintVshRoundRef.current(0) : null;
      if (firstLanding && firstLanding.position) {
        animateSpin({ level: playerLevel, position: firstLanding.position }, { silent: true });
      } else {
        const seq0 = sequenceRef.current[0];
        animateSpin(seq0 ? { level: playerLevel, position: seq0.position } : undefined, { silent: true });
      }
    } else {
      animateSpin(undefined, { silent: true });
    }
    window.setTimeout(() => {
      const s = cardSoundRef.current || {};
      if (s.name) playSound(s.name, s.volume);
    }, 380);
    resetTimer();
    if (mode !== "vs_robot" && mode !== "vs_human") startTimer();
  };

  // Helper: did the current cycle of progress complete one SUPERT?
  const isOneCycleComplete = (prog, mode_) => {
    const letters = prog.filter((p) => p !== "*");
    const stars = prog.filter((p) => p === "*").length;
    if (mode_ === "sem_sorte") return letters.length >= 6;
    const required = ["S", "U", "P", "E", "R", "T"];
    const have = new Set(letters);
    const missing = required.filter((l) => !have.has(l)).length;
    return missing <= stars && (have.size + stars) >= 6;
  };

  // Grand finale confetti — fireVictoryConfetti() is imported from
  // components/gameplay/celebrations.js
  // SUPERT mid-cycle confetti — fireSupertConfetti(ref) is imported too.

  // Run pause sequence: flash supert letters, 5-4-3-2-1 countdown, then resume
  const runPauseSequence = (onComplete) => {
    setSupertFlash(true);
    setPauseCountdown(5);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setPauseCountdown((c) => {
        if (c === null) return null;
        if (c <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setPauseCountdown(null);
          setSupertFlash(false);
          setTimeout(() => onComplete(), 100);
          return null;
        }
        return c - 1;
      });
    }, 1000);
  };

  const finishCompetition = async (status, finalTime) => {
    // Wrapped via Result Outbox: if the network hiccups at end-of-game the
    // result is queued in localStorage and retried in background.
    const result = await submitResult("/play/finish", {
      session_id: session.session_id,
      game_id: gameId,
      time_seconds: finalTime,
      status,
    }, { description: `Competição ${status}` });
    const data = result.response || {};
    if (mode === "competition") {
      setFinishData(data);
      if (result.queued) {
        toast.info("Resultado guardado offline — será enviado assim que houver ligação");
      }
      // Fetch full history
      if (auth?.credential_id) {
        try {
          const h = await api.get(`/credentials/${auth.credential_id}/attempts`);
          setHistory(h.data);
        } catch (e) { console.error("[credentials.attempts]", e); }
      }
      // Note: the transition from "won"/"lost" → "exhausted" is now driven
      // by an explicit "continue" button on the Won/Lost screen (item 2 of
      // the Fev-2026 follow-ups). Previously we auto-advanced 1800ms after
      // the final attempt, which didn't give the player time to read the
      // "Vitória / Tentativas restantes" summary.
    }
    if (mode === "eliminatoria") {
      // Carry passed/finalist flags into the post-game screen via finishData.
      setFinishData(data);
      if (data?.is_new_finalist) {
        // EPIC celebration — bigger confetti + finalist fanfare.
        try { fireFinalistConfetti(); } catch (e) { /* noop */ }
        try { playFinalistFanfare(); } catch (e) { /* noop */ }
      } else if (data?.passed) {
        try { fireVictoryConfetti(); } catch (e) { /* noop */ }
      }
    }
    return data;
  };

  const playAgainSameGame = async () => {
    try {
      setBusy(true);
      stopTimer();
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
      // VS ROBOT: re-fetch /vs-robot/start for a fresh question pool.
      if (mode === "vs_robot") {
        const robotId = params.get("robot");
        const elimId = params.get("elim");
        const elimAttemptId = params.get("elim_attempt");
        const tierId = params.get("tier");
        // If this is a "Vencer Robot" eliminatoria attempt, retrying means
        // burning a NEW attempt. Call the elim start endpoint to create one.
        if (elimId && elimAttemptId) {
          try {
            const { data } = await api.post(
              `/eliminatorias/${elimId}/play/start`,
              null,
              { params: { lang, ...(tierId ? { tier_id: tierId } : {}) } },
            );
            const robot = data?.eliminatoria?.target_robot;
            const newAttempt = data?.attempt_id || (data?.session_id || "").replace(/^elim-/, "");
            if (!robot || !newAttempt) throw new Error("missing robot/attempt");
            // Persist the tier-resolved level so the new vs_robot duel uses the
            // correct difficulty.
            const newLevel = Number(data?.level || data?.eliminatoria?.level || 1) || 1;
            sessionStorage.setItem("player_level", String(newLevel));
            // Refresh URL to point at the new attempt id (so finish handlers record it).
            const usp = new URLSearchParams();
            usp.set("mode", "vs_robot");
            usp.set("robot", robot.id);
            usp.set("elim", elimId);
            usp.set("elim_attempt", newAttempt);
            if (tierId) usp.set("tier", tierId);
            usp.set("level", String(newLevel));
            nav(`/jogar/${gameId}?${usp.toString()}`, { replace: true });
            // Now fetch a fresh vs_robot session to seed local state.
            const fresh = await api.get("/vs-robot/start", {
              params: { game_id: gameId, robot_id: robot.id, lang, elim: elimId },
            });
            setSession(fresh.data);
            prefetchAnswerKey(gameId);
            setPhase("ready");
            setProgress([]); resetVsRobotState();
            userClickRef.current = null; roundResolvedRef.current = false;
            setReveal(null); setFeedback(null);
            setRound(0); resetTimer();
            setQuadriculas([null, null, null, null, null]);
            setSupertCycle(0); setPauseCountdown(null); setSupertFlash(false);
            setRepeatsSoFar(0); setStreak(0);
            setFinishData(null);
            return;
          } catch (err) {
            const detail = err?.response?.data?.detail;
            // Most likely: 429 = attempts exhausted, or 409 = already passed.
            if (typeof detail === "string") toast.info(detail);
            nav("/eliminatorias/lobby");
            return;
          }
        }
        const elimIdRestart = params.get("elim") || undefined;
        const { data } = await api.get("/vs-robot/start", {
          params: { game_id: gameId, robot_id: robotId, lang, ...(elimIdRestart ? { elim: elimIdRestart } : {}) },
        });
        setSession(data);
        setPhase("ready");
        setProgress([]); resetVsRobotState();
        userClickRef.current = null; roundResolvedRef.current = false;
        setReveal(null); setFeedback(null);
        setRound(0); resetTimer();
        setQuadriculas([null, null, null, null, null]);
        setSupertCycle(0); setPauseCountdown(null); setSupertFlash(false);
        setRepeatsSoFar(0); setStreak(0);
        setFinishData(null);
        return;
      }
      const payload = { game_id: gameId, mode };
      // GRANDE FINAL (treino): preserve the `gf=1` flag on replay so the
      // backend keeps bypassing credentials and allowing competition-status
      // games. Without this, "Jogar de novo" would fail with
      // "Jogo não disponível em treino".
      const isGrandeFinalReplay = params.get("gf") === "1";
      if (isGrandeFinalReplay) payload.grande_final = true;
      if (mode === "training") {
        payload.name = profile.name;
        payload.school = profile.school || "";
        payload.nationality = profile.nationality;
        payload.training_user_id = sessionStorage.getItem("training_user_id");
      } else if (mode === "eliminatoria") {
        const elimId = params.get("elim");
        const tierIdParam = params.get("tier");
        if (!elimId) throw new Error("missing elim id");
        // Re-attempt: pass tier_id so the new attempt is scoped to the same tier
        // AND so the backend can re-resolve the level. Also persist level here.
        const { data: ed } = await api.post(
          `/eliminatorias/${elimId}/play/start`,
          null,
          { params: { lang, ...(tierIdParam ? { tier_id: tierIdParam } : {}) } },
        );
        const newLevel = Number(ed?.level || ed?.eliminatoria?.level || 1) || 1;
        sessionStorage.setItem("player_level", String(newLevel));
        setSession(ed);
        prefetchAnswerKey(gameId);
        setPhase("ready");
        setProgress([]); setReveal(null); setFeedback(null);
        setRound(0); resetTimer();
        setQuadriculas([null, null, null, null, null]);
        setSupertCycle(0); setPauseCountdown(null); setSupertFlash(false);
        setRepeatsSoFar(0); setStreak(0);
        setFinishData(null); setHistory(null);
        return;
      } else {
        payload.credential_id = auth?.credential_id;
      }
      const { data } = await api.post("/play/start", payload, { params: { lang } });
      setSession(data);
      prefetchAnswerKey(gameId);
      setPhase("ready");
      setProgress([]);
      setReveal(null);
      setFeedback(null);
      setRound(0);
      resetTimer();
      setQuadriculas([null, null, null, null, null]);
      setSupertCycle(0);
      setPauseCountdown(null);
      setSupertFlash(false);
      setRepeatsSoFar(0);
      setStreak(0);
      setFinishData(null);
      setHistory(null);
    } catch (e) {
      // attempts exhausted or other error
      const detail = e?.response?.data?.detail;
      const isExhausted =
        (typeof detail === "object" && detail?.code === "no_attempts") ||
        (typeof detail === "string" && /sem tentativas|tentativas|esgotadas|attempts|intentos/i.test(detail));
      const msg = typeof detail === "object" ? (detail.message || "Erro") : (detail || "Erro");
      toast.error(msg);
      if (mode === "competition" && isExhausted) {
        setPhase("exhausted");
        // load history so user sees full summary
        if (auth?.credential_id) {
          try {
            const h = await api.get(`/credentials/${auth.credential_id}/attempts`);
            setHistory(h.data);
          } catch (e) { console.error("[credentials.attempts]", e); }
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const activePosition = quadriculas[playerLevel - 1];
  // Sync `progress` to its ref so async timer-callbacks read fresh values.
  useEffect(() => { progressRef.current = progress; }, [progress]);

  // ============================================================================
  // VS ROBOT — round scheduling + resolver + finish persistence.
  // All mode-specific state lives inside the hook; we expose only what the
  // render layer + `answer()` need below.
  // ============================================================================
  const vsr = useVsRobotDuel({
    enabled: mode === "vs_robot",
    session, phase, spinning, activePosition, round, repeatsSoFar,
    sharedRefs: { userClickRef, roundResolvedRef, roundStartRef, progressRef, activeQuestionRef },
    setProgress, setStreak, setPhase, setBusy, setFeedback, setReveal, setRound,
    animateSpin,
  });
  const {
    robotProgress, vsrOutcome, vsrMyRank, vsrShareOpen, vsrFloating, vsrRobotFx,
    setVsrShareOpen, setUserClickInfo, userClickInfo,
    robotProgressRef, robotScheduledRef, robotTimerRef, vsrFinishedRef,
    resolveRound: resolveVsRobotRound,
    reset: resetVsRobotState,
  } = vsr;

  // ---- VS ROBOT: persist end-of-duel result (leaderboard OR elim attempt) ----
  useVsRobotFinish({
    mode, phase, session, params,
    gameId, profile,
    progress, robotProgress, round,
    finishedRef: vsrFinishedRef,
    onRank: vsr.setVsrMyRank,
    onElimFinish: setFinishData,
  });

  // ============================================================================
  // VS HUMANO — WS subscriber, round resolver, finish persistence, disconnect
  // grace. The WebSocket connection lives in the App-level provider
  // (VsHumanProvider) so it survives navigation from /vs-humano → /jogar.
  // ============================================================================
  const vsh = useVsHumanDuel({
    enabled: mode === "vs_human",
    vshCtx, session, phase, spinning, round, playerLevel,
    gameId, profile, repeatsSoFar,
    sharedRefs: { userClickRef, roundResolvedRef, roundStartRef, progressRef, activeQuestionRef },
    setProgress, setStreak, setPhase, setBusy, setFeedback, setReveal, setRound,
    animateSpin,
    paintVshRoundRef,
  });
  const {
    vshOpponent, oppProgress, vshOutcome, vshFloating, vshFx, vshAbandoned,
    setVshOpponent, setVshFloating, reset: resetVsHumanState,
    sequenceRef, oppProgressRef, oppClickRef: vshOppClickRef, vshFinishedRef, roundDeadlineRef,
    resolveRound: resolveVsHumanRound,
  } = vsh;

  // Display label for the OPPONENT'S escalão/nível (VS HUMANO only — since
  // 2026-02 each peer plays their own level). Must be declared AFTER
  // `useVsHumanDuel` because it reads `vshOpponent` from the hook. Falls
  // back to the player's own label when opponent level/tier didn't propagate.
  const opponentLevelLabel = (() => {
    if (mode !== "vs_human") return null;
    const game = session?.game;
    const oppLevel = Number(vshOpponent?.level) || Number(playerLevel) || 1;
    const oppTierId = vshOpponent?.tier_id || null;
    if (game?.level_system === "tiers" && Array.isArray(game.tiers)) {
      let tier = oppTierId && game.tiers.find((tt) => tt.id === oppTierId);
      if (!tier) tier = game.tiers.find((tt) => Number(tt.level) === Number(oppLevel));
      if (tier?.name) {
        const lvl = Number(tier.level || oppLevel);
        const tierName = (tier.translations?.[lang]?.name) || tier.name;
        return `${tierName} (${t("level_paren")} ${lvl})`;
      }
    }
    return `${t("level_paren")} ${oppLevel}`;
  })();

  // Roulette display + snippet state (depends on vsh.sequenceRef for VS HUMAN).
  const {
    displayedByPos,
    snippetsByPos: rouletteSnippetsByPos,
    nextRound: rouletteNextRound,
    paintVshRound,
    hasNext: rouletteHasNext,
  } = useRouletteSnippets({
    session, phase, spinning, activePosition, mode, sequenceRef,
  });
  // Refs so the stable `animateSpin` callback (empty deps) can consult them.
  const rouletteNextRoundRef = useRef(rouletteNextRound);
  useEffect(() => { rouletteNextRoundRef.current = rouletteNextRound; }, [rouletteNextRound]);
  useEffect(() => { paintVshRoundRef.current = paintVshRound; }, [paintVshRound]);
  // Active question — no longer gated on `!spinning`. The spin animation was
  // deprecated to be instant (single rAF flip) precisely so downstream hooks
  // could keep observing the true→false transition; blocking rendering during
  // that microsecond used to leave the answer buttons showing "—" on cold
  // mounts (VS Robot / VS Humano, where the initial render commits the robot
  // avatar + WS listeners in the same batch and can delay the rAF). Now the
  // seeded question is exposed the moment `activePosition` is set and stays
  // stable because `useRouletteSnippets` preserves the seed on first spin.
  const activeQuestion = activePosition ? displayedByPos[activePosition] : null;
  // Mirror activeQuestion into the ref used by VS-ROBOT's robot-timer callback
  // so a robot-first outcome can still reveal the correct option index in
  // the grid even though the user never clicked (setState wouldn't flush in
  // time for the setTimeout).
  useEffect(() => { activeQuestionRef.current = activeQuestion; }, [activeQuestion]);

  // Watch opponent floating awards — when a `was_repeat` award appears,
  // trigger the adjacent-duplicate animation in the opponent's SupertBar.
  useEffect(() => {
    const opp = mode === "vs_robot" ? vsrFloating?.robot : mode === "vs_human" ? vshFloating?.opponent : null;
    if (!opp || opp.length === 0) return;
    const rep = opp.find((a) => a.was_repeat && a.item !== "*");
    if (!rep) return;
    setOppRepeatLetter(rep.item);
    const t = setTimeout(() => setOppRepeatLetter(null), 1200);
    return () => clearTimeout(t);
  }, [mode, vsrFloating?.robot, vshFloating?.opponent]);

  // Same watcher for the LOCAL player in VS ROBOT / VS HUMANO — mirrors
  // the VS TEMPO effect where a duplicate super-letter briefly appears
  // over the player's SupertBar and fades out (letting them see they
  // "earned" it but with no lasting value). VS TEMPO already handles this
  // inline in `handleTrainingAnswer` because awards are computed there; in
  // VS ROBOT / VS HUMANO the awards are computed inside the duel hooks and
  // surfaced via `vsrFloating.user` / `vshFloating.user`.
  useEffect(() => {
    const mine = mode === "vs_robot" ? vsrFloating?.user : mode === "vs_human" ? vshFloating?.user : null;
    if (!mine || mine.length === 0) return;
    const rep = mine.find((a) => a.was_repeat && a.item !== "*");
    if (!rep) return;
    setRepeatLetter(rep.item);
    // Same "poof" audio cue that VS TEMPO already plays when the player wins
    // a duplicate letter — reinforces the visual "you got it but you already
    // had it" feedback so the outcome is unmissable in VS ROBOT / VS HUMANO
    // too. Falls back to the built-in `oops` if the game has no custom sound.
    playSound(
      session?.game?.repeat_sound || "oops",
      session?.game?.repeat_sound_volume ?? 0.7,
    );
    const t = setTimeout(() => setRepeatLetter(null), 1200);
    return () => clearTimeout(t);
  }, [mode, vsrFloating?.user, vshFloating?.user, session?.game?.repeat_sound, session?.game?.repeat_sound_volume]);

  // DEMO auto-play — orchestrates the guide phrases for the current round.
  //
  // The pacing is DRIVEN BY the phrases' configured `duration_ms` (from
  // the admin backoffice). Frase N+1 only appears AFTER frase N has
  // fully disappeared — a small `FADE_GAP` covers the framer-motion
  // exit animation so consecutive phrases never overlap on screen. The
  // auto-click on the correct answer fires only AFTER phrase_3 has
  // disappeared as well.
  //
  // IMPORTANT: we deliberately DO NOT return a cleanup that clears the
  // auto-click timers. `activeQuestion` may change reference on every
  // spin frame, and a naive cleanup would blow away the pending click
  // before it ever fires. The `demoTimersRef` list is cleared globally
  // on unmount + by the SAIR DEMO button.
  const demoLastRoundRef = useRef(-1);
  useEffect(() => {
    if (!isDemoMode) return;
    if (demoFinishedRef.current) return; // DEMO terminou (SUPERT completo) — não voltar a mostrar frases
    if (phase !== "playing") return;
    if (spinning) return;
    if (!activeQuestion) return;
    if (busy) return;
    if (feedback) return;
    if (!demoRunner.phrasesByKey || !demoRunner.phrasesByKey.phrase_1) return;
    if (demoLastRoundRef.current === round) return;
    // Wait for any lingering phrase (e.g. phrase_4_win from the previous
    // round) to fully hide BEFORE starting this round's phrase_1. This
    // matches the admin backoffice contract: "a frase seguinte só vem
    // depois da anterior ter desaparecido". Once currentPhrase flips to
    // null, this effect re-runs and the sequence starts.
    if (demoRunner.currentPhrase) return;
    demoLastRoundRef.current = round;

    const FADE_GAP = 450; // matches DemoOverlay's motion exit duration
    const durationOf = (key) => {
      const p = demoRunner.phrasesByKey[key];
      return p && p.enabled !== false ? (Number(p.duration_ms) || 2200) : 0;
    };

    let cursor = 0;
    for (const key of ["phrase_1", "phrase_2", "phrase_3"]) {
      const dur = durationOf(key);
      if (dur <= 0) continue; // phrase disabled → skip cleanly
      const showAt = cursor;
      const hideAt = cursor + dur;
      const tShow = setTimeout(() => demoRunner.showPhrase(key), showAt);
      const tHide = setTimeout(() => demoRunner.hidePhrase(), hideAt);
      demoTimersRef.current.push(tShow, tHide);
      cursor = hideAt + FADE_GAP;
    }

    const tClick = setTimeout(() => {
      // Auto-click the correct answer. Resolve the correct index at CLICK
      // TIME (not at effect setup time) because the roulette may re-land
      // on a different position during the guide phase — using a stale
      // correctIdx would submit a wrong answer.
      demoRunner.hidePhrase();
      const q = activeQuestionRef.current;
      const idx = peekCorrectIndex(gameId, {
        questionId: q?.id,
        position: q?.position || activePosition,
      }) || Number(q?.correct ?? q?.correct_index ?? 1) || 1;
      if (answerFnRef.current) answerFnRef.current(idx);
    }, cursor);
    demoTimersRef.current.push(tClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoMode, phase, spinning, activeQuestion, busy, feedback, round, demoRunner.phrasesByKey, demoRunner.currentPhrase]);

  // VS HUMANO — 3-2-1 countdown state. Both peers show the same overlay
  // immediately after `match_start` arrives (session + opponent + sequence
  // are all present). At 0 we transition to "VAMOS!" for a beat and then
  // call `beginGame()` so the first question appears fresh.
  const [vshCountdown, setVshCountdown] = useState(null); // null | 3 | 2 | 1 | 0
  // Competition mode: 5-4-3-2-1 countdown after "INICIAR JOGO" is pressed.
  const [compCountdown, setCompCountdown] = useState(null); // null | 5..1 | 0 (GO!)
  useEffect(() => {
    if (compCountdown === null) return undefined;
    if (compCountdown === 0) {
      const t = setTimeout(() => {
        setCompCountdown(null);
        beginGame();
      }, 600);
      return () => clearTimeout(t);
    }
    try { playSound("swipe", 0.35); } catch { /* ignore */ }
    const t = setTimeout(() => setCompCountdown((n) => (n === null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compCountdown]);
  useEffect(() => {
    if (mode !== "vs_human" || phase !== "ready") return undefined;
    if (!session?.session_id || !vshOpponent || sequenceRef.current.length === 0) return undefined;
    if (vshCountdown !== null) return undefined;
    setVshCountdown(3);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, phase, session?.session_id, vshOpponent]);
  useEffect(() => {
    if (mode !== "vs_human") return undefined;
    if (vshCountdown === null) return undefined;
    if (vshCountdown === 0) {
      // Show "VAMOS!" briefly, then start the game.
      const t = setTimeout(() => {
        setVshCountdown(null);
        beginGame();
      }, 700);
      return () => clearTimeout(t);
    }
    // Optional tick sound — quiet, using existing "swipe" sound if present.
    try { playSound("swipe", 0.35); } catch { /* ignore */ }
    const t = setTimeout(() => setVshCountdown((n) => (n === null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, vshCountdown]);

  // NOTE: We DO NOT send `leave` from a useEffect cleanup here. React 18
  // StrictMode (active in dev) runs cleanups TWICE on mount (mount → cleanup
  // → re-mount), which would broadcast `opponent_left` to the other peer
  // IMMEDIATELY after the duel screen mounts. Instead, `leave` is sent ONLY
  // from explicit user-initiated exits (`exitGame()`), and when the user
  // closes the tab/browser the backend handles the WS disconnect natively
  // and broadcasts `opponent_left` via `_drop_peer`.

  const exitGame = async () => {
    stopTimer();
    if (robotTimerRef.current) clearTimeout(robotTimerRef.current);
    if (roundDeadlineRef.current) { clearTimeout(roundDeadlineRef.current); roundDeadlineRef.current = null; }
    // VS HUMANO: notify the peer only when the duel is still live.
    // After phase === "won"/"lost" the duel has already ended naturally and
    // both peers are on the result screen — a `leave` would be noise.
    if (mode === "vs_human" && phase !== "won" && phase !== "lost") {
      try { vshCtx.send({ type: "leave" }); } catch { /* ignore */ }
    }
    if (phase === "playing" && session && mode !== "vs_robot" && mode !== "vs_human") {
      // Competition: sair no meio de uma tentativa CONTA como derrota
      // (usa uma tentativa e regista defeat). Treino: mantém-se `abandoned`.
      const finishStatus = mode === "competition" ? "lost" : "abandoned";
      await submitResult("/play/finish", {
        session_id: session.session_id,
        game_id: gameId,
        time_seconds: getTimerElapsed(),
        status: finishStatus,
      }, { description: finishStatus === "lost" ? "Saída antes de terminar (competição — conta como derrota)" : "Saída antes de terminar" });
    }
    if (mode === "competition") {
      logout();
    }
    // Training: keep identity in sessionStorage so user can play other games/levels.
    // It will clear automatically when the tab closes, or via "Editar" in TrainStart.
    nav(getExitRoute(mode, params));
  };

  // "Voltar" — go to the IMMEDIATELY PREVIOUS screen using the browser
  // history (never logs the user out). Cleans up the active session the same
  // way `exitGame` does so stats / state stay consistent, then `nav(-1)`.
  const backToMenu = async () => {
    stopTimer();
    if (robotTimerRef.current) clearTimeout(robotTimerRef.current);
    if (roundDeadlineRef.current) { clearTimeout(roundDeadlineRef.current); roundDeadlineRef.current = null; }
    // Same rule as exitGame: only notify the peer while the duel is live.
    if (mode === "vs_human" && phase !== "won" && phase !== "lost") {
      try { vshCtx.send({ type: "leave" }); } catch { /* ignore */ }
    }
    if (phase === "playing" && session && mode !== "vs_robot" && mode !== "vs_human") {
      // Same rule as exitGame: competition mid-game "Voltar" = derrota.
      const finishStatus = mode === "competition" ? "lost" : "abandoned";
      await submitResult("/play/finish", {
        session_id: session.session_id,
        game_id: gameId,
        time_seconds: getTimerElapsed(),
        status: finishStatus,
      }, { description: finishStatus === "lost" ? "Voltar ao menu (competição — conta como derrota)" : "Voltar ao menu" });
    }
    // History-based back. Fallback to the mode's canonical menu when the user
    // landed here from outside the app (no previous entry in history).
    if (window.history.length > 1) {
      nav(-1);
    } else {
      nav(getBackRoute(mode, params));
    }
  };

  // `confirmAction` holds the pending user-initiated exit action so the
  // same confirmation modal serves both "Voltar" and "Sair" buttons.
  const [confirmAction, setConfirmAction] = useState(null); // "back" | "exit" | null
  const confirmExit = confirmAction !== null;
  const setConfirmExit = (v) => setConfirmAction(v ? "exit" : null);
  const requestExit = () => {
    // DEMO: a tutorial run never counts as a defeat — bypass the confirm
    // dialog and exit immediately, cleaning any lingering demo state.
    if (isDemoMode) {
      clearDemoTimers();
      demoRunner.hidePhrase();
      setIsDemoMode(false);
      stopTimer();
      nav(getBackRoute(mode, params));
      return;
    }
    if (phase === "playing") setConfirmAction("exit");
    else exitGame();
  };
  const requestBack = () => {
    // DEMO: tutorial exit skips the confirm dialog in the same way as
    // `requestExit` (see note above). Same behaviour for all exit paths.
    if (isDemoMode) {
      clearDemoTimers();
      demoRunner.hidePhrase();
      setIsDemoMode(false);
      stopTimer();
      nav(getBackRoute(mode, params));
      return;
    }
    if (phase === "playing") setConfirmAction("back");
    else backToMenu();
  };

  // ============================================================================
  // ANSWER HANDLERS — dispatched by mode. Each sub-handler owns its own
  // round-resolution flow (training/competition/eliminatoria are merged into
  // the "training" branch because they share the same time-based + lives
  // mechanic; their only divergence is the `/play/finish` payload, handled
  // by `finishCompetition`).
  // ============================================================================

  // VS HUMANO — SYMMETRIC race: both peers share the same question deck
  // and race for each round. Local click info is captured here; the actual
  // round-resolution happens inside the hook when the server's `round_result`
  // arrives (first answer decides — mirrors VS ROBOT). Letters/stars are
  // rolled server-side so both devices show identical collections.
  // VS HUMANO rematch: called by the WonScreen / LostScreen "Desforra" (or
  // "Jogar de novo") button. Fires the invite over the SAME WebSocket that
  // was used during the just-finished duel — no need for the opponent to
  // return to the /vs-humano lobby first. The App-level
  // <GlobalVsHumanRematch/> listener picks up the invite on the opponent's
  // side and shows the amber dialog anywhere in the app.
  const sendVsHumanRematchInvite = (senderWon = false) => {
    const opp = vshOpponent?.user_id;
    if (!opp || !vshCtx?.send) {
      // Fallback: fall back to the classic navigate-to-lobby flow if the
      // socket disappeared (e.g. after a long idle timeout).
      const q = new URLSearchParams({
        rematch_to: vshOpponent?.user_id || "",
        rematch_name: vshOpponent?.name || "",
        rematch_game_id: gameId || "",
      });
      nav(`/vs-humano?${q.toString()}`);
      return;
    }
    // Send both messages BEFORE navigating — the ws is App-level so `send`
    // survives the /jogar unmount, but the previous setTimeout-based delay
    // was unreliable (winner could time out before the invite actually
    // dispatched, leaving the loser without a dialog and the winner stuck
    // on "A aguardar..."). The backend processes WS frames FIFO so back-
    // to-back `leave` + `invite` works: by the time the invite is validated
    // the target's `session_id` has been cleared by the automatic
    // `sendLeaveAfterFinish` (fired ~1.5s after phase change) OR by the
    // opponent-side `opponent_left` broadcast that our own `leave` above
    // triggered.
    try { vshCtx.send({ type: "leave" }); } catch { /* ignore */ }
    // `sender_won` lets the opponent's global rematch dialog render the
    // right copy: winner→loser invite shows "Jogar de novo?" (friendly);
    // loser→winner invite shows "Desforra?" (revenge). The backend just
    // forwards this flag to the target.
    try { vshCtx.send({ type: "invite", to: opp, rematch: true, sender_won: !!senderWon }); } catch { /* ignore */ }
    // Navigate ourselves back to /vs-humano so we're ready to receive the
    // match_start (VsHumano's handler prepares sessionStorage for GamePlay).
    // The `rematch_pending=1` flag tells the lobby's auto-invite effect that
    // an invite is already in flight, so it does NOT try to re-fire once
    // the opponent shows up.
    const q = new URLSearchParams({
      rematch_to: vshOpponent?.user_id || "",
      rematch_name: vshOpponent?.name || "",
      rematch_game_id: gameId || "",
      rematch_pending: "1",
    });
    nav(`/vs-humano?${q.toString()}`);
  };

  const handleVsHumanAnswer = async (idx) => {
    if (userClickRef.current || roundResolvedRef.current) return;
    setBusy(true);
    const userTimeMs = performance.now() - roundStartRef.current;
    // Capture click FIRST (feedback + ref) — server will echo `answer_result`
    // shortly with the authoritative correctness flag.
    const correctIdx = (activeQuestion?.correct ?? activeQuestion?.correct_index);
    const isCorrect = Number(correctIdx) === Number(idx);
    userClickRef.current = { idx, time: userTimeMs, correct: isCorrect };
    setFeedback({ correct: isCorrect, correctIndex: correctIdx, chosen: idx });
    try {
      vshCtx.send({
        type: "answer",
        round,
        position: activePosition,
        question_id: activeQuestion?.id,
        answer_index: idx,
        elapsed_seconds: userTimeMs / 1000,
      });
    } catch { /* socket may be down */ }
    // The server decides the round on the FIRST answer and broadcasts
    // `round_result` (gains + letters for both) — the hook resolves there.
    setBusy(false);
  };

  // VS ROBOT — symmetric race against the simulated robot deadline. The user
  // click is captured here; the actual round-resolution happens either in
  // the robot's setTimeout or — if we can short-circuit — IMMEDIATELY via
  // `resolveVsRobotRound(robotInfo)` (early-resolve path).
  const handleVsRobotAnswer = async (idx) => {
    if (userClickInfo || roundResolvedRef.current) return;
    setBusy(true);
    const userTimeMs = performance.now() - roundStartRef.current;
    try {
      const data = await resolveAnswer({
        gameId,
        sessionId: session.session_id,
        position: activePosition,
        questionId: activeQuestion?.id,
        answerIndex: idx,
        elapsedSeconds: userTimeMs / 1000,
      });
      setFeedback({ correct: data.correct, correctIndex: data.correct_index, chosen: idx });
      userClickRef.current = { idx, time: userTimeMs, correct: data.correct };
      setUserClickInfo({ idx, time: userTimeMs, correct: data.correct });
      // Early resolve: skip waiting for the robot timer when there is no
      // chance of being "simultaneous" (delta > simultaneous_ms).
      const robotInfo = robotScheduledRef.current;
      const simulMs = Math.max(0, Math.min(2000, Number(session?.game?.simultaneous_ms ?? 500)));
      if (robotInfo && !roundResolvedRef.current && (robotInfo.time - userTimeMs) > simulMs) {
        if (robotTimerRef.current) { clearTimeout(robotTimerRef.current); robotTimerRef.current = null; }
        resolveVsRobotRound(robotInfo);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setBusy(false);
    }
  };

  // TRAINING / COMPETITION / ELIMINATORIA — time-based, supports the LIVES
  // mechanic + SUPERT cycle progression. Posts the answer, applies lives /
  // penalty / game-over, awards a letter on success, advances the cycle or
  // finalises the run (via `finishCompetition` for competition/elim).
  const handleTrainingAnswer = async (idx) => {
    setBusy(true);
    const elapsed = getTimerElapsed();
    // FAIR TIMER (Fase A): freeze the running clock at the exact instant of
    // the click. It resumes only when the NEXT question is on screen (see
    // `advanceRound` below). This ensures the total time submitted at the
    // end of the game counts ONLY pure human reaction time — never the
    // network roundtrip nor the inter-round `round_delay_ms` gap.
    pauseTimer();
    try {
      const data = await resolveAnswer({
        gameId,
        sessionId: session.session_id,
        position: activePosition,
        questionId: activeQuestion?.id,
        answerIndex: idx,
        elapsedSeconds: elapsed,
      });
      setFeedback({ correct: data.correct, correctIndex: data.correct_index, chosen: idx });
      if (data.correct) {
        playSound(session?.game?.correct_sound || "correct", session?.game?.correct_sound_volume);
        setStreak((s) => s + 1);
      } else {
        playSound(session?.game?.wrong_sound || "wrong", session?.game?.wrong_sound_volume);
        setStreak(0);
      }

      if (!data.correct) {
        // LIVES system — only the time-based modes use it.
        const usesLives = mode === "training" || mode === "competition" || mode === "eliminatoria";
        const totalLives = usesLives ? Math.max(1, Number(session?.game?.lives ?? 1)) : 1;
        const penalty = Math.max(0, Number(session?.game?.time_penalty_seconds ?? 5));
        const used = livesUsed + 1;
        if (usesLives && used < totalLives) {
          // Forgiving mode: pause clock, add penalty, show "−1 vida" modal.
          pauseTimer();
          addTimerPenalty(penalty);
          setLivesUsed(used);
          playSound("whoosh", 0.45);
          setTimerPenaltyFx({ active: true, amount: penalty });
          setTimeout(() => {
            setTimerPenaltyFx({ active: false, amount: 0 });
            setLivesModal({ used, total: totalLives, penalty });
          }, 150);
          setBusy(false);
          return;
        }
        // No lives left → game over.
        stopTimer();
        const finalTime = elapsed;
        if (mode === "competition" || mode === "eliminatoria") {
          await finishCompetition("lost", finalTime);
        } else {
          await submitResult("/play/finish", {
            session_id: session.session_id,
            game_id: gameId,
            time_seconds: finalTime,
            status: "lost",
          }, { description: "Derrota (sem vidas)" });
        }
        setTimeout(() => {
          playSound(session?.game?.defeat_sound || "defeat", session?.game?.defeat_sound_volume);
          setPhase("lost");
        }, 1200);
        setBusy(false);
        return;
      }

      // OPTIMISTIC LOCAL AWARD — mirror the backend `supert_award` so the
      // round can advance IMMEDIATELY (no 100–500 ms network wait). The HTTP
      // call to /play/award is still fired fire-and-forget for stats/logs.
      //
      // DEMO ("Aprender a Jogar"): we override the random award with a
      // deterministic script (S,U,P,E,P-repeat,★,R) so the tutorial finishes
      // fast and visits every UX branch — normal letter, repeated letter,
      // star wildcard, final SUPERT completion.
      const localAward = isDemoMode
        ? demoRunner.getScriptedAward(demoRoundRef.current, progress)
        : localSupertAward({
            progress,
            mode: session.game.mode || "normal",
            repeats_so_far: repeatsSoFar,
            max_repeated_letters: session.game.max_repeated_letters || 0,
          });
      const awardedLetter = localAward.item;
      const newProgress = localAward.progress;
      if (isDemoMode) demoRoundRef.current += 1;
      // Fire-and-forget server award (best-effort logging only)
      api.post("/play/award", {
        progress,
        mode: session.game.mode,
        repeats_so_far: repeatsSoFar,
        max_repeated_letters: session.game.max_repeated_letters || 0,
      }).catch(() => { /* ignore — local award is authoritative for UX */ });
      setProgress(newProgress);
      if (localAward.was_repeat) {
        setRepeatLetter(awardedLetter);
        setRepeatsSoFar((n) => n + 1);
        playSound(
          session?.game?.repeat_sound || "oops",
          session?.game?.repeat_sound_volume ?? 0.7
        );
        setTimeout(() => setRepeatLetter(null), 2400);
      }

      // Advance IMMEDIATELY (no setTimeout gate) so the next roulette card
      // and question appear on the very next frame after the click.
      const advanceRound = async () => {
        // DEMO: always target a single SUPERT cycle regardless of the
        // origin game's supert_count so the tutorial ends fast.
        const targetCycles = isDemoMode ? 1 : (session.game.supert_count || 1);
        // Pre-compute whether THIS answer closes the final SUPERT. Needed
        // so DEMO can skip the intermediate "phrase_4_*" when the next
        // guide to show is "phrase_5_final" (completing SUPERT). Without
        // this, phrase_4 would appear 2s AFTER phrase_5_final and the
        // tutorial would end on the wrong message.
        const willFinalWin =
          isOneCycleComplete(newProgress, session.game.mode) &&
          (supertCycle + 1) >= targetCycles;
        // DEMO: show the post-answer guide phrase (super-letter / star /
        // repeat). Duration comes from the phrase's own `duration_ms`
        // configured in the admin, so admins fully control the pacing.
        // The phrase is DELAYED by 2 seconds so the green "correct"
        // feedback stays alone on screen first (user requested: "quando a
        // opção certa fica verde, deve ficar verde durante 2 segundos
        // antes de aparecer a próxima frase").
        if (isDemoMode && !willFinalWin) {
          const key = localAward.was_repeat
            ? "phrase_4_repeat"
            : localAward.item === "*"
            ? "phrase_4_star"
            : "phrase_4_win";
          const p = demoRunner.phrasesByKey?.[key];
          if (p && p.enabled !== false) {
            const showT = setTimeout(() => demoRunner.showPhrase(key), 2000);
            const dur = Number(p.duration_ms) || 2200;
            const hideT = setTimeout(() => demoRunner.hidePhrase(), 2000 + dur);
            demoTimersRef.current.push(showT, hideT);
          }
        }
        if (isOneCycleComplete(newProgress, session.game.mode)) {
          const nextCycle = supertCycle + 1;
          if (nextCycle >= targetCycles) {
            // FINAL WIN
            stopTimer();
            const finalTime = getTimerElapsed();
            // DEMO ("Aprender a Jogar"): NEVER submit a result — the tutorial
            // is anonymous and must not touch rankings, credentials or
            // training_users. Just celebrate visually and let the WonScreen
            // render for a friendly exit back to the menu.
            const submitPromise = isDemoMode
              ? Promise.resolve(null)
              : (mode === "competition" || mode === "eliminatoria")
              ? finishCompetition("won", finalTime)
              : submitResult("/play/finish", {
                  session_id: session.session_id,
                  game_id: gameId,
                  time_seconds: finalTime,
                  status: "won",
                  name: profile.name,
                  school: profile.school || "",
                  nationality: profile.nationality,
                  training_user_id: sessionStorage.getItem("training_user_id"),
                  level: playerLevel,
                  // Tier the player was in when this record was set. Sent so
                  // the public rankings can distinguish tiers that share the
                  // same numeric level (e.g. 4.º ano and 5.º ano both at 3).
                  tier_id:
                    params.get("tier") ||
                    (mode === "competition"
                      ? (auth?.tier_id || auth?.credential?.tier_id)
                      : sessionStorage.getItem("player_tier_id")) ||
                    null,
                }, { description: `Vitória — ${profile.name} (${finalTime.toFixed(2)}s)` })
                  .then((winRes) => {
                    if (winRes?.queued) {
                      toast.info("Resultado guardado offline — será enviado assim que houver ligação");
                    }
                    return winRes;
                  });
            playSound(session?.game?.victory_sound || "victory", session?.game?.victory_sound_volume);
            // DEMO: lock the tutorial here — cancel any pending phrase
            // timers (phrase_4_* scheduled by earlier rounds, hide
            // callbacks, etc.) and flag the auto-player so it will NOT
            // schedule phrase_1 → phrase_3 again. Without this guard the
            // tutorial would re-loop into phrase_1 after phrase_5_final.
            if (isDemoMode) {
              demoFinishedRef.current = true;
              clearDemoTimers();
            }
            // FINAL SUPERT CELEBRATION — 3s "ondulante" wave on the mini
            // cards (same effect used between cycles when supert_count > 1)
            // so the player has time to look at the profile / SupertBar and
            // see the completed SUPERT before the finish modal covers it.
            // We deliberately do NOT render the big "SUPERT!" overlay on
            // top of the roulette/central cards — the user asked for ONLY
            // the mini thumbnails above the profile with the wave effect.
            setSupertFlash(true);
            fireSupertConfetti(supertBarRef);
            // Personal celebration — "Parabéns, {nome}!" during the 3s wave
            // for VS Humano wins (user request 2026-02). Competition mode was
            // removed per user request (Feb 2026) — the big yellow name shown
            // after every attempt was distracting.
            if (mode === "vs_human") {
              setCelebratingFinalWin(true);
            }
            // DEMO: show the "Completaste SUPERT!" phrase during the whole
            // celebration window so the tutorial finishes with a clear
            // "objective reached" message.
            if (isDemoMode) demoRunner.showPhrase("phrase_5_final");
            setTimeout(() => {
              // DEMO: keep the wave animation and the big SUPERT! letters
              // frozen on-screen so the user can look for as long as they
              // want. Everything is cleared when they tap SAIR DEMO (which
              // navigates away and unmounts this whole tree).
              if (!isDemoMode) {
                setCelebratingSupert(false);
                setSupertFlash(false);
                setCelebratingFinalWin(false);
              }
              // Eliminatória owns its own celebration via finishCompetition;
              // training/competition fall back to the legacy confetti.
              if (mode !== "eliminatoria" && !isDemoMode) fireVictoryConfetti();
              // Ensure the submission has landed before we swap into "won"
              // (so finishData is populated for the modal). If the network
              // is slow we still cap the wait at the celebration window.
              Promise.resolve(submitPromise).finally(() => {
                if (isDemoMode) {
                  // DEMO: keep the "Completaste SUPERT!" celebration visible
                  // indefinitely. The tutorial only ends when the user
                  // taps SAIR DEMO — this gives them all the time they
                  // need to admire the completed collection and read the
                  // final phrase without being rushed off the screen.
                  setBusy(false);
                } else {
                  setPhase("won");
                  setBusy(false);
                }
              });
            }, 3000);
          } else {
            // One SUPERT done — celebrate for 2s, then pause + countdown.
            setSupertCycle(nextCycle);
            pauseTimer();
            playSound("supert", session?.game?.card_sound_volume);
            setCelebratingSupert(true);
            setSupertFlash(true);
            fireSupertConfetti(supertBarRef);
            setTimeout(() => {
              // Fade out the "SUPERT!" celebration first (framer-motion exit
              // takes ~400ms). Only AFTER that we swap into the pause phase
              // and start the countdown, so the two never overlap on screen.
              setCelebratingSupert(false);
              setSupertFlash(false);
              setTimeout(() => {
                setPhase("paused");
                runPauseSequence(() => {
                  setProgress([]);
                  setReveal(null);
                  setFeedback(null);
                  setPhase("playing");
                  setRound((r) => r + 1);
                  resumeTimer();
                  animateSpin();
                });
                setBusy(false);
              }, 500);
            }, 2000);
          }
        } else {
          // Continue same cycle — apply the awarded letter as the next card spins in.
          // Small delay so the RED/GREEN feedback on the answered option button
          // is actually visible to the human eye before the option grid
          // re-renders with the NEXT question's options. Configurable per-game
          // via `Game.round_delay_ms` (0..5000 ms, default 100). Prevents the
          // "clicked the previous question's answer, hit the new one" bug the
          // teacher reported in VS Robot where the robot can flip the round
          // very fast.
          const roundDelayMs = isDemoMode
            // DEMO: hold the green "correct" state visible for 2 seconds so
            // the player clearly sees which option was chosen BEFORE the
            // next frase_4 / phrase_1 sequence takes over. Overrides the
            // game's `round_delay_ms` which is tuned for live play (100ms).
            ? 2000
            : Math.max(0, Math.min(5000, Number(session?.game?.round_delay_ms ?? 100)));
          setTimeout(() => {
            setFeedback(null);
            setReveal(awardedLetter);
            setRound((r) => r + 1);
            // (card sound is played inside animateSpin at the exact commit frame)
            animateSpin();
            // FAIR TIMER (Fase A): the new question is now committed and the
            // spin flips to visible within 1–2 rAF frames. Unfreeze the clock
            // here so measurement resumes at question-visible, not before.
            resumeTimer();
            setBusy(false);
          }, roundDelayMs);
        }
      };
      advanceRound();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
      // FAIR TIMER (Fase A): the answer POST failed — resume the clock so
      // the user isn't stuck with a frozen timer if they retry.
      resumeTimer();
      setBusy(false);
    }
  };

  // Dispatcher — picks the per-mode handler. Guards (busy / phase / spinning)
  // live here so each handler is free to assume "we are clear to handle a click".
  // Uses `answerInFlightRef` as a synchronous gate that DOES NOT depend on
  // React state batching — this fixes a rare bug where a click was visually
  // registered (button pressed animation) but the answer was silently dropped
  // because the guard read a still-stale `busy`/`spinning` in the closure.
  // Stable `answer` callback — MUST NOT change identity between renders, else
  // memoized <Options /> will re-render on every timer tick.
  const answerFnRef = useRef(null);
  const pendingClickRef = useRef(null);          // { idx, tries } — deferred click
  const pendingTimerRef = useRef(null);
  const answer = useCallback((idx) => {
    if (answerFnRef.current) answerFnRef.current(idx);
  }, []);
  // Rebuild the actual handler on every render (it closes over state), but
  // stash it in a ref so the stable `answer` above always sees the latest.
  answerFnRef.current = (idx) => {
    if (answerInFlightRef.current) return;
    // If we're in a transitional state (round change, spin, question about
    // to arrive) the button may already look enabled to the eye but the
    // React state hasn't caught up. Instead of silently dropping the click,
    // queue it and retry a couple of times over ~200 ms. This eliminates the
    // "had to click twice" UX in fast-paced modes (VS TEMPO / VS ROBOT /
    // VS HUMANO). Non-recoverable states (won/lost/paused) are still dropped.
    const transient = busy || !activeQuestion;
    const nonRecoverable = phase !== "playing";
    if (nonRecoverable) return;
    if (transient) {
      if (pendingClickRef.current && pendingClickRef.current.idx === idx) return;
      pendingClickRef.current = { idx, tries: 0 };
      const tick = () => {
        const p = pendingClickRef.current;
        if (!p) return;
        p.tries += 1;
        // Latest closure via `answer` (which reads answerFnRef.current)
        if (p.tries > 6) { pendingClickRef.current = null; return; }
        // Retry via the latest handler snapshot
        pendingTimerRef.current = setTimeout(() => {
          pendingClickRef.current = null;
          if (answerFnRef.current) answerFnRef.current(p.idx);
        }, 35);  // ~2 frames
      };
      tick();
      return;
    }
    // Clear any pending deferred click since we're actually going through.
    if (pendingTimerRef.current) { clearTimeout(pendingTimerRef.current); pendingTimerRef.current = null; }
    pendingClickRef.current = null;

    answerInFlightRef.current = true;
    const safetyTimer = setTimeout(() => { answerInFlightRef.current = false; }, 3000);
    let ret;
    try {
      if (mode === "vs_human") ret = handleVsHumanAnswer(idx);
      else if (mode === "vs_robot") ret = handleVsRobotAnswer(idx);
      else ret = handleTrainingAnswer(idx);
    } catch (e) {
      console.error("[answer]", e);
      clearTimeout(safetyTimer);
      answerInFlightRef.current = false;
      return;
    }
    Promise.resolve(ret).finally(() => {
      clearTimeout(safetyTimer);
      answerInFlightRef.current = false;
    });
  };

  // Auto-logout after 20 seconds on "tentativas esgotadas" screen (competition only)
  useEffect(() => {
    if (phase !== "exhausted" || mode !== "competition") return;
    const t = setTimeout(() => {
      logout();
      nav("/");
    }, 20000);
    return () => clearTimeout(t);
  }, [phase, mode, logout, nav]);

  if (!session) {
    return (
      <div className="h-[100dvh] grid place-items-center font-[Nunito] bg-gradient-to-b from-sky-400 to-sky-200 p-6">
        <style>{`
          @keyframes supert-loadbar { 0%{width:8%} 25%{width:42%} 55%{width:68%} 80%{width:85%} 100%{width:94%} }
          @keyframes supert-loadstripes { from{background-position:0 0} to{background-position:30px 0} }
        `}</style>
        <div className="w-full max-w-xs text-center" data-testid="game-loading">
          <Loader2 className="w-11 h-11 text-white mx-auto mb-4 animate-spin drop-shadow" />
          <div className="text-white font-[Fredoka] font-extrabold text-xl mb-1 drop-shadow" data-testid="game-loading-title">
            {t("preparing_game")}
          </div>
          <div className="text-white/90 text-sm mb-5">{t("preparing_game_hint")}</div>
          <div className="h-3.5 w-full rounded-full bg-white/35 overflow-hidden shadow-inner" data-testid="game-loading-bar">
            <div
              className="h-full rounded-full"
              style={{
                animation: "supert-loadbar 5s ease-out forwards, supert-loadstripes 0.8s linear infinite",
                backgroundColor: "#ffffff",
                backgroundImage:
                  "linear-gradient(45deg, rgba(14,165,233,0.55) 25%, transparent 25%, transparent 50%, rgba(14,165,233,0.55) 50%, rgba(14,165,233,0.55) 75%, transparent 75%, transparent)",
                backgroundSize: "30px 30px",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // If we entered the page already in 'exhausted' phase (no attempts), render only the modal
  if (phase === "exhausted") {
    return (
      <div className="h-[100dvh] grid place-items-center font-[Nunito] bg-gradient-to-b from-sky-400 to-sky-200 p-4">
        <Modal>
          <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-3" />
          <h2 className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-slate-900 mb-2" data-testid="exhausted-title">
            {t("attempts_exhausted")}
          </h2>
          <p className="text-slate-700 text-sm md:text-base">{t("thanks_participation")}</p>
          {history && <ActivitySummary history={history} />}
          <p className="text-xs text-slate-500 mt-3">{t("session_ends_10s")}</p>
          <div className="flex flex-col gap-2 mt-3">
            <Button
              data-testid="btn-logout-exhausted"
              onClick={() => { logout(); nav("/"); }}
              className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-bold"
            >
              <LogOut className="w-4 h-4 mr-2" /> {t("common_exit")}
            </Button>
          </div>
        </Modal>
      </div>
    );
  }

  const visual = session?.game?.visual || {};
  const bgCss = getBackgroundCss(visual);
  const isImage = (visual.bg_type === "image") && visual.bg_image_url;

  return (
    <>
    <div
      data-testid="gameplay-root"
      className="h-[100dvh] flex flex-col relative overflow-hidden font-[Nunito]"
      style={{
        background: bgCss,
        backgroundSize: isImage ? "cover" : undefined,
        backgroundPosition: isImage ? "center" : undefined,
      }}
    >
      {/* Diagonal stripes overlay only for default sky gradient */}
      {visual.bg_type !== "pattern" && visual.bg_type !== "image" && (
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(45deg, rgba(255,255,255,0.4) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.4) 75%, transparent 75%, transparent)",
            backgroundSize: "60px 60px",
          }}
        />
      )}

      {/* Gameplay chrome: compact brand block (ring + logo + brand text)
       * in the top-left corner. Mirrors the HomeHeader / GameHeader look so
       * players keep the same brand identity during play. The full
       * navigation/SAIR remain hidden — surfaced via the bottom GameToolbar.
       * In embed mode (home-page tablet overlay) the whole chrome is hidden.
       */}
      {!isEmbed && <GameplayBrandCorner cfg={homeCfg} lang={lang} />}

      {/* Floating Logout (icon only) — top-right corner, visible ONLY in
          landscape phones where the regular Header is hidden via CSS. The
          large in-game toolbar at the bottom keeps the full Logout button
          for desktop/portrait. */}
      {!isEmbed && (
        <div className="absolute top-3 right-3 z-40 landscape-only-logout">
          <LogoutButton variant="icon" showLabel={false} />
        </div>
      )}

      {/* EXIT pill - bottom-left, with SOUND toggle right next to it */}
      <GameToolbar onBack={requestBack} onExit={requestExit} muted={muted} isDemoMode={isDemoMode} />

      <main className="relative z-10 flex-1 px-3 md:px-6 py-2 grid grid-cols-12 gap-3 md:gap-5 min-h-0">

        {/* LEFT: Roulette card (rotated) — hidden during ready phase */}
        <div className="col-span-12 md:col-span-3 flex items-center justify-center md:justify-end min-h-0" data-testid="anchor-roulette-card">
          {phase !== "ready" && (() => {
            const fmt = session.game.format || "original";
            const levelsCount = fmt === "quiz" ? 4 : 5;
            // Pass the FULL quadriculas array (stable ref between renders)
            // so React.memo shallow-compare works. RouletteCard already
            // reads only the first `levelsCount` entries.
            return (
            <RouletteCard
              quadriculas={quadriculas}
              reveal={reveal}
              playerLevel={playerLevel}
              round={round}
              spinning={spinning}
              effect={session.game.roulette_effect || "slide_left"}
              displayedByPos={displayedByPos}
              snippetsByPos={rouletteSnippetsByPos}
              cardLogo={cardLogo}
              cardLogoCfg={cardLogoCfg}
              visual={session.game.visual}
              lang={lang}
              activePosition={activePosition}
              format={fmt}
              initialIdleChar={initialIdleCharRef.current}
            />
            );
          })()}
        </div>

        {/* CENTER: Question card + options — card hidden during ready phase */}
        <div className={`col-span-12 md:col-span-6 flex flex-col items-stretch justify-start min-h-0 ${(mode === "vs_robot" || mode === "vs_human") ? "gap-2 md:gap-3 pt-0" : "gap-6 md:gap-8 pt-2"}`}>
          {mode === "vs_robot" && phase !== "ready" && (
            <div className="relative z-20">
              <RaceBar
                userName={profile.name}
                userAvatarNode={<div className="w-9 h-9 rounded-full overflow-hidden bg-slate-100 border-2 border-white shadow shrink-0 grid place-items-center"><AvatarSVG cfg={avatarCfg} size={32} /></div>}
                userProgress={progress}
                robotName={session.robot?.name}
                robotImage={session.robot?.image_url}
                robotProgress={robotProgress}
                supertCount={session.game.supert_count || 1}
                robotFx={vsrRobotFx}
              />
            </div>
          )}
          {mode === "vs_human" && phase !== "ready" && (
            <div className="relative z-20">
              <RaceBar
                userName={profile.name}
                userAvatarNode={<div className="w-9 h-9 rounded-full overflow-hidden bg-slate-100 border-2 border-white shadow shrink-0 grid place-items-center"><AvatarSVG cfg={avatarCfg} size={32} /></div>}
                userProgress={progress}
                robotName={vshOpponent?.name || t("opponent_default")}
                robotAvatarNode={<div className="w-9 h-9 rounded-full overflow-hidden bg-violet-100 border-2 border-white shadow shrink-0 grid place-items-center"><AvatarSVG cfg={vshOpponent?.avatar_cfg || avatarCfg} size={32} /></div>}
                robotProgress={oppProgress}
                supertCount={session.game.supert_count || 1}
                robotFx={vshFx}
                oppTestIdSide="opponent"
              />
              {vshOutcome?.text && (
                <div className="mt-1 text-center text-xs font-bold font-[Fredoka] text-slate-700 bg-white/80 rounded-full px-3 py-1 inline-block shadow-sm" data-testid="vsh-outcome">
                  {vshOutcome.text}
                </div>
              )}
            </div>
          )}
          {phase !== "ready" && (
            <div data-testid="anchor-main-card">
              <CardFront
                questions={displayedByPos}
                activePosition={activePosition}
                feedback={feedback}
                round={round}
                spinning={spinning}
                effect={session.game.card_effect || "fade"}
                visual={session.game.visual}
                cardLogo={cardLogo}
                cardLogoCfg={cardLogoCfg}
                format={session.game.format || "original"}
                demoHighlight={isDemoMode && demoRunner.currentPhrase?.key === "phrase_2" ? activePosition : null}
              />
            </div>
          )}
          {phase !== "ready" && (
            <div className="mx-auto w-full max-w-[600px]">
              <Options
                question={activeQuestion}
                feedback={feedback}
                onAnswer={answer}
                disabled={busy || phase !== "playing" || isDemoMode}
              />
            </div>
          )}
        </div>

        {/* RIGHT: timer/robot + SUPERT + profile */}
        <div className={`col-span-12 md:col-span-3 flex flex-col gap-2 min-h-0 ${(mode === "vs_robot" || mode === "vs_human") ? "pt-0 relative z-20" : "pt-2"}`}>
          {mode === "vs_robot" ? (
            <>
              <RobotProfileBox robot={session.robot} levelLabel={levelLabel} fx={vsrRobotFx} awards={vsrFloating.robot} pillsCfg={homeCfg.gameplay_pills} lang={lang} />
              <SupertBar
                progress={robotProgress}
                flash={false}
                supertCycle={0}
                supertCount={session.game.supert_count || 1}
                repeatLetter={oppRepeatLetter}
                cardNumbers={session.game.visual?.card_position_numbers}
                shimmer={session.game.visual?.supert_card_shimmer ?? true}
              />
            </>
          ) : mode === "vs_human" ? (
            <>
              <HumanOpponentBox opponent={vshOpponent} levelLabel={opponentLevelLabel || levelLabel} fx={vshFx} awards={vshFloating.opponent} pillsCfg={homeCfg.gameplay_pills} lang={lang} />
              <SupertBar
                progress={oppProgress}
                flash={false}
                supertCycle={0}
                supertCount={session.game.supert_count || 1}
                repeatLetter={oppRepeatLetter}
                cardNumbers={session.game.visual?.card_position_numbers}
                shimmer={session.game.visual?.supert_card_shimmer ?? true}
              />
            </>
          ) : mode === "competition" && session.game?.max_attempts ? (
            <AttemptsPill
              current={Math.max(1, (session.game.max_attempts || 1) - (session.attempts_available ?? 0))}
              max={session.game.max_attempts}
            />
          ) : null}
          {mode !== "vs_robot" && mode !== "vs_human" && (
            <TimerPill
              getElapsed={getTimerElapsed}
              level={playerLevel}
              levelLabel={levelLabel}
              penaltyPulse={timerPenaltyFx.active}
              penaltyAmount={timerPenaltyFx.amount}
              pillsCfg={homeCfg.gameplay_pills}
              lang={lang}
            />
          )}
          {/* Spacer pushes SUPERT bar + Profile to the bottom of the column */}
          <div className="flex-1" aria-hidden />
          <div
            ref={supertBarRef}
            className="relative transition-transform duration-300"
            style={{ transform: celebratingSupert ? "scale(1.05)" : "scale(1)" }}
          >
            <SupertBar
              progress={progress}
              flash={supertFlash}
              supertCycle={supertCycle}
              supertCount={session.game.supert_count || 1}
              repeatLetter={repeatLetter}
              cardNumbers={session.game.visual?.card_position_numbers}
              shimmer={session.game.visual?.supert_card_shimmer ?? true}
            />
          </div>
          <div className="relative">
            <ProfileCard
              name={profile.name}
              nationality={profile.nationality}
              school={profile.school}
              avatarCfg={avatarCfg}
              onEditAvatar={() => setShowAvatarPicker(true)}
              gameName={session.game.name}
              xp={xp}
              level={playerLevel}
              levelLabel={levelLabel}
              bestTime={bestTime}
            />
            {/*
              FloatingAwards junto ao perfil do jogador foram removidas por
              pedido do produto (Fev 2026) — as mini-cartas ganhadas já são
              visíveis na SupertBar; a duplicação em cima do perfil era ruído
              visual, especialmente em VS Robot / VS Humano onde o adversário
              também tinha o seu próprio floating. A lógica de prémios em
              useVsRobotDuel / useVsHumanDuel mantém-se — só o rendering foi
              retirado.
            */}
          </div>
        </div>
      </main>

      {showAvatarPicker && (
        <AvatarBuilder
          current={avatarCfg}
          credentialId={auth?.credential_id}
          trainingUserId={mode === "training" ? sessionStorage.getItem("training_user_id") : undefined}
          onSave={(cfg) => {
            setAvatarCfg(cfg);
            sessionStorage.setItem("player_avatar_cfg", JSON.stringify(cfg));
            // Persist on backend (best-effort, ignore failures)
            if (auth?.credential_id) {
              api.put(`/credentials/${auth.credential_id}/avatar`, { avatar_cfg: cfg }).catch((err) => { console.error("[credentials.avatar]", err); });
            } else if (mode === "training") {
              const tid = sessionStorage.getItem("training_user_id");
              if (tid) api.put(`/training-users/${tid}/avatar`, { avatar_cfg: cfg }).catch((err) => { console.error("[training-users.avatar]", err); });
            }
            setShowAvatarPicker(false);
          }}
          onClose={() => setShowAvatarPicker(false)}
        />
      )}

      {confirmExit && (
        <ConfirmExitModal
          action={confirmAction}
          onCancel={() => setConfirmAction(null)}
          onConfirm={(action) => {
            setConfirmAction(null);
            if (action === "back") backToMenu();
            else exitGame();
          }}
        />
      )}

      {/* Lives lost modal — wrong answer when game has more than 1 life. */}
      <LivesLostModal
        info={livesModal}
        onContinue={() => {
          // KEEP the same question. The player retries the SAME card until
          // they either answer correctly (green feedback + spin to next) or
          // run out of lives (defeat). We only clear the feedback + reveal so
          // the option buttons become clickable again, and resume the timer
          // (elapsed time + penalty were preserved during the pause).
          setLivesModal(null);
          setFeedback(null);
          setReveal(null);
          resumeTimer();
        }}
      />


      <AnimatePresence>
        {phase === "ready" && mode === "vs_human" && vshCountdown !== null && (
          <VsHumanCountdown321
            value={vshCountdown}
            playerName={profile.name}
            playerAvatar={avatarCfg}
            opponentName={vshOpponent?.name}
            opponentAvatar={vshOpponent?.avatar_cfg}
          />
        )}

        {celebratingSupert && (
          // Big pulsating celebration — sits above the SUPERT bar during
          // the 3-second celebration window. In DEMO mode, we render the
          // word "SUPERT!" using the same brand mini-cards (S,U,P,E,R,T)
          // players collect on the SupertBar, followed by a giant "!" —
          // reinforcing the connection between the collected letters and
          // the goal. In normal gameplay we keep the classic pulsating
          // text variant which is quicker and less visually noisy.
          <motion.div
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: [0.4, 1.15, 1, 1.08, 1], opacity: 1 }}
            exit={{ scale: 1.4, opacity: 0 }}
            transition={{ duration: 1.8, ease: "easeOut", times: [0, 0.3, 0.55, 0.8, 1] }}
            className="pointer-events-none fixed inset-x-0 top-[15%] z-[55] grid place-items-center"
            data-testid="supert-celebration-text"
          >
            {isDemoMode ? (
              <div
                className="flex items-end gap-1 md:gap-3"
                style={{
                  filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.55)) drop-shadow(0 0 40px rgba(251,191,36,0.7))",
                  animation: "supert-pulse 700ms ease-in-out infinite alternate",
                }}
                data-testid="supert-celebration-cards"
              >
                {["S", "U", "P", "E", "R", "T"].map((L, i) => (
                  <motion.img
                    key={L}
                    src={BRAND_ASSETS[L]}
                    alt={L}
                    initial={{ y: -40, opacity: 0, rotate: -12, scale: 0.6 }}
                    animate={{ y: 0, opacity: 1, rotate: 0, scale: 1 }}
                    transition={{ delay: 0.1 + i * 0.09, duration: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
                    style={{
                      height: "clamp(90px, 15vw, 200px)",
                      width: "auto",
                      display: "block",
                    }}
                  />
                ))}
                <motion.div
                  initial={{ scale: 0, rotate: -20 }}
                  animate={{ scale: [0, 1.4, 1], rotate: [-20, 10, 0] }}
                  transition={{ delay: 0.85, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
                  className="font-[Fredoka] font-black italic text-amber-300 leading-none pl-1 md:pl-2"
                  style={{
                    fontSize: "clamp(90px, 15vw, 200px)",
                    textShadow: "6px 6px 0 rgba(0,0,0,0.4), 0 0 40px rgba(251,191,36,0.9)",
                    WebkitTextStroke: "3px #1f2937",
                  }}
                >
                  !
                </motion.div>
              </div>
            ) : (
              <div
                className="font-[Fredoka] font-black italic text-amber-300 leading-none tracking-wider"
                style={{
                  fontSize: "clamp(64px, 12vw, 160px)",
                  textShadow: "6px 6px 0 rgba(0,0,0,0.35), 0 0 40px rgba(251,191,36,0.65)",
                  WebkitTextStroke: "3px #1f2937",
                  animation: "supert-pulse 700ms ease-in-out infinite alternate",
                }}
              >
                SUPERT!
              </div>
            )}
          </motion.div>
        )}

        {celebratingFinalWin && (
          // Personal win celebration — "Parabéns, {nome}!" shown ABOVE the
          // SUPERT wave for competition + VS Humano modes during the 3s
          // post-SUPERT window (user request 2026-02).
          <motion.div
            key="final-win-personal"
            initial={{ scale: 0.5, opacity: 0, y: -20 }}
            animate={{ scale: [0.5, 1.15, 1, 1.06, 1], opacity: 1, y: 0 }}
            exit={{ scale: 1.3, opacity: 0, y: -30 }}
            transition={{ duration: 1.6, ease: "easeOut", times: [0, 0.3, 0.55, 0.8, 1] }}
            className="pointer-events-none fixed inset-x-0 top-[8%] z-[56] grid place-items-center px-4"
            data-testid="final-win-personal-celebration"
          >
            <div
              className="font-[Fredoka] font-black italic text-amber-300 leading-tight tracking-wide text-center"
              style={{
                fontSize: "clamp(42px, 8vw, 120px)",
                textShadow: "6px 6px 0 rgba(0,0,0,0.4), 0 0 40px rgba(251,191,36,0.85)",
                WebkitTextStroke: "3px #1f2937",
                animation: "supert-pulse 700ms ease-in-out infinite alternate",
              }}
            >
              {t("congrats_name_bang", { name: profile?.name || "" })}
            </div>
          </motion.div>
        )}

        {phase === "ready" && !(mode === "vs_human") && !isEmbed && (
          <ReadyModal
            session={session}
            mode={mode}
            avatarCfg={avatarCfg}
            levelLabel={levelLabel}
            onBack={backToMenu}
            onStart={startWhenReady}
            onDemo={mode === "training" && params.get("gf") !== "1" ? startDemo : undefined}
            starting={preparing}
          />
        )}

        {/* DEMO guide phrases — only visible during the "Aprender a Jogar" run. */}
        {isDemoMode && <DemoOverlay phrase={demoRunner.currentPhrase} /> }

        {/* DEMO exit button — floating, top-right. Allows the user to leave
            the tutorial at any moment without waiting for the SUPERT to
            complete. Bypasses the standard "back to menu" confirmation so a
            single tap sends them home. Only visible while the demo is
            actively playing (phase === "playing"). */}
        {isDemoMode && phase === "playing" && (
          <motion.button
            data-testid="btn-demo-exit"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.35 }}
            onClick={() => {
              clearDemoTimers();
              demoRunner.hidePhrase();
              setIsDemoMode(false);
              stopTimer();
              nav(getBackRoute(mode, params));
            }}
            className="fixed top-4 right-4 z-[75] inline-flex items-center gap-1.5 rounded-full bg-black/70 hover:bg-black/85 text-white font-[Fredoka] font-bold text-xs md:text-sm px-3 py-1.5 md:px-4 md:py-2 border border-white/25 shadow-lg backdrop-blur-sm transition"
          >
            <X className="w-3.5 h-3.5 md:w-4 md:h-4" />
            <span>Sair DEMO</span>
          </motion.button>
        )}

        {phase === "ready" && mode === "competition" && compCountdown !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] grid place-items-center bg-gradient-to-br from-sky-700/95 via-indigo-700/95 to-fuchsia-700/95 backdrop-blur-sm"
            data-testid="comp-countdown-overlay"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={compCountdown}
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.6, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                className="font-[Fredoka] font-black text-white leading-none drop-shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                style={{ fontSize: compCountdown === 0 ? "clamp(64px, 16vw, 200px)" : "clamp(120px, 24vw, 300px)" }}
                data-testid={`comp-countdown-${compCountdown === 0 ? "go" : compCountdown}`}
              >
                {compCountdown === 0 ? t("countdown_go") : compCountdown}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}

        {phase === "won" && !isEmbed && (
          <WonScreen
            mode={mode}
            time={getTimerElapsed()}
            finishData={finishData}
            history={history}
            busy={busy}
            credentialId={auth?.credential_id}
            gameId={gameId}
            playerName={profile.name}
            elimInfo={elimInfo}
            onPlayAgain={playAgainSameGame}
            onChooseOtherGame={() => nav(getOtherGameRoute(mode, params))}
            onExit={() => { if (mode === "competition") { logout(); } nav("/"); }}
            onContinueExhausted={
              mode === "competition" && (finishData?.attempts_available ?? 1) <= 0
                ? () => setPhase("exhausted")
                : undefined
            }
            vsRobot={
              mode === "vs_robot" ? {
                robotName: session?.robot?.name,
                mentalStrength: session?.robot?.mental_strength ?? 5,
                pointsDelta: +2 * (session?.robot?.mental_strength ?? 5),
                myRank: vsrMyRank,
                onShare: () => setVsrShareOpen(true),
                elim: params.get("elim") ? { passed: true } : null,
              } :
              mode === "vs_human" ? {
                robotName: vshOpponent?.name,
                abandoned: vshAbandoned,
                opponentUserId: vshOpponent?.user_id,
                onRematch: () => sendVsHumanRematchInvite(true),
              } :
              undefined
            }
          />
        )}

        {phase === "lost" && !isEmbed && (
          <LostScreen
            mode={mode}
            finishData={finishData}
            history={history}
            busy={busy}
            gameId={gameId}
            playerName={profile.name}
            onPlayAgain={playAgainSameGame}
            onChooseOtherGame={() => nav(getOtherGameRoute(mode, params))}
            onExit={() => { if (mode === "competition") { logout(); } nav("/"); }}
            onContinueExhausted={
              mode === "competition" && (finishData?.attempts_available ?? 1) <= 0
                ? () => setPhase("exhausted")
                : undefined
            }
            vsRobot={
              mode === "vs_robot" ? {
                robotName: session?.robot?.name,
                mentalStrength: session?.robot?.mental_strength ?? 5,
                pointsDelta: -0.5 * (session?.robot?.mental_strength ?? 5),
                myRank: vsrMyRank,
                elim: params.get("elim") ? { passed: false } : null,
              } :
              mode === "vs_human" ? {
                robotName: vshOpponent?.name,
                abandoned: vshAbandoned,
                opponentUserId: vshOpponent?.user_id,
                onRematch: () => sendVsHumanRematchInvite(false),
              } :
              undefined
            }
          />
        )}

        {phase === "exhausted" && (
          <ExhaustedScreen
            history={history}
            onExit={() => { logout(); nav("/"); }}
          />
        )}
      </AnimatePresence>

      {/* PAUSE overlay (between SUPERT cycles) */}
      <PauseOverlay
        visible={phase === "paused"}
        pauseCountdown={pauseCountdown}
        supertCycle={supertCycle}
        supertCount={session?.game?.supert_count || 1}
      />

      {/* VS ROBOT — share rank dialog */}
      {mode === "vs_robot" && vsrShareOpen && vsrMyRank && (
        <RankShareDialog
          playerName={profile.name}
          robotId={params.get("robot")}
          robotName={session?.robot?.name}
          rank={vsrMyRank.rank}
          totalPlayers={vsrMyRank.total_players}
          onClose={() => setVsrShareOpen(false)}
        />
      )}
    </div>
    </>
  );
}
